import { LinkedProjectConfig, ProjectCheckFinding, ProjectCheckResult, ServiceItem, ServiceTransaction } from '../../types';
import { asDay, buchungstag, isBackfilledAltEntry, isManualPosting, isPostingEntry, loggedQuantity, reportNumberOf } from '../serviceLogUtils';
import { reportRefOf } from '../reportBooking';
// Derselbe Leistungsbegriff wie im Rücknahme-Dialog: was dort als Zwilling
// zur Wahl steht, meldet hier die Regel `doppelte_buchung` — und nichts
// anderes. Ein zweiter Begriff davon liefe früher oder später auseinander.
import { reversibleEntries } from '../serviceReversalLogic';
// Der Verzicht, wie ihn `planReversal` rechnet — dieselbe Funktion, dieselbe
// Herkunft. Siehe `zwillingSchonAbgerechnet`.
import { waivedQuantity } from '../projectLv';
import { UnbookedSentReportRow, istAusgeglichen, istTagGedeckt, meldetNoch } from '../unbookedReportsLogic';
import { TAGESPAUSCHALE, billingModeAt } from '../linkedBillingMode';
// Nur der Typ: `projectCheckLogic` bleibt die Stelle, an der ein
// Leistungszeitraum definiert wird. Ein zweiter Begriff davon waere ein
// zweiter Wahrheitsbegriff.
import type { IssuedPeriod } from '../projectCheckLogic';
import {
    Abrechnungslage,
    IntegrityFinding,
    IntegrityReportRow,
    IntegrityScopeKind,
    IntegritySeverity,
} from './integrityTypes';

/**
 * Der Integritätswächter — die Regeln.
 *
 * Pure Funktionen, „heute" wird hereingereicht: kein Supabase, kein
 * `Date.now()`, kein React. Genau deshalb ist der Nachtlauf mit demselben
 * Code prüfbar wie die Oberfläche — und genau deshalb kann der Nachtlauf
 * dieselbe Regelmaschine benutzen, statt eine zweite zu bekommen.
 *
 * DIESES MODUL BAUT NICHTS NACH. `projectCheckLogic` bleibt die einzige
 * Stelle, an der über Mengen geurteilt wird; hier werden seine Befunde nur
 * in eine Form gebracht, die einen Fensterwechsel überlebt. Dazu kommen
 * SIEBEN Lagen, die im Bestand von keiner Regel erfasst werden:
 *
 *   `doppelte_archivzeile`  — Vorfall 6164: zwei Archivzeilen mit gleichem
 *   Datum UND gleicher Uhrzeit ließen die Tagesregel des Leistungsnachweises
 *   eine bezahlte Begehung auf null setzen.
 *
 *   `doppelte_buchung`      — Vorfall vom 02.09.2026 (6110 #25 dreimal, 6185
 *   #22 zweimal, 6051 #135 zweimal): derselbe Bericht auf dieselbe Position
 *   mehrfach gebucht, die Zwillinge nur Millisekunden auseinander. Je Zwilling
 *   ein Begehungspreis zu viel auf der nächsten Rechnung — und aufgefallen nur
 *   zufällig über die Drift-Sicht des Journals.
 *
 *   `bericht_auf_zwei_positionen` — Vorfall 6051 #149 (29.09.2026):
 *   derselbe Bericht auf ZWEI Positionen, aus zwei Vorgängen (Fix-SQL auf
 *   2.7.60, Nachhol-Marker auf 2.7.20). `doppelte_buchung` gruppiert je
 *   Position und schwieg. Eine Pauschale zu viel auf der Rechnung.
 *
 *   `tageszwilling_ohne_bericht` — Vorfall 6221 (07.09.2026): zwei
 *   Leistungszeilen derselben Begehungsposition am selben Tag, von denen
 *   keine einen zweiten Bericht nennt — eine `LEGACY-AUTO`-Zeile des
 *   Versandpfads vom März und die Handbuchung „Bericht #2 abgeschlossen" vom
 *   Juni. `doppelte_buchung` sieht das nicht, sie hängt am `reportRef`, und
 *   den trägt die Altzeile nicht. 13 statt 11 auf der Rechnung.
 *
 *   `eintrag_ohne_leistung` — Vorfall 26021023 (Einheitenwechsel Stunden →
 *   Stück): die Altposition wurde gelöscht, die Protokolleinträge zeigten
 *   danach ins Leere. Zwanzig Fehlalarme, und die Buchungen liefen ins Nichts.
 *
 *   `gebucht_ohne_archiv`   — Vorfälle 5634 #49 und 5944 #40: der Bericht ging
 *   raus und wurde gebucht, dann brach die Versandkette ab. Die Forderung
 *   stand in der Abrechnung, der Nachweis nirgends. Die Gegenrichtung
 *   (versendet, nicht gebucht) meldet `v_unbooked_sent_reports` seit Nr. 45 —
 *   diese hier sah niemand.
 *
 *   `versendet_ohne_archiv` — Vorfall 6051 #149 (28.09.2026): die Mail ging
 *   raus, danach wurde weder gebucht noch archiviert. Die Sicht sucht nur
 *   unter ARCHIVIERTEN Berichten, `gebucht_ohne_archiv` nur unter GEBUCHTEN —
 *   einen Bericht ohne beides sah keine der beiden. Diese Regel geht deshalb
 *   vom Sendeverlauf aus.
 *
 * Dazu zwei Regeln aus der Sicht `v_versendete_maengel_verlust` (Nr. 139,
 * Abschnitt 4): `versendeter_mangel_verschwunden` und `versendetes_foto_fehlt`
 * — Vorfall 26041008 #8, ein älterer Stand überschrieb nach dem Versand.
 *
 * Ausdrücklich KEINE neuen Mengenregeln — die hat der Projektcheck,
 * einschließlich der dort dokumentierten Entscheidungen (Mehrmenge ist
 * abrechenbar, `unlogged_quantity` ist bewusst gelb und keine Sperre).
 */

// ═══════════════════════════════════════════════════════════════════════════
// Hilfen
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Dieselbe Toleranz wie in `allocationLogic`. Mengen entstehen hier aus
 * Summen von Teilmengen; ohne sie erschiene ein Gleitkommarest (0,0000000001)
 * als Menge, die ins Leere zeigt.
 */
export const EPS = 0.0005;

const round3 = (value: number): number => {
    const gerundet = Math.round(value * 1000) / 1000;
    // -0 und 0 sind derselbe Stand. Als Zeichenkette wären sie es nicht, und
    // der Fingerabdruck ließe den Befund grundlos wieder aufleben.
    return Object.is(gerundet, -0) ? 0 : gerundet;
};

/** Kalendertag im deutschen Format, ohne `new Date` — die Datei bleibt zeitzonenfrei. */
const formatDay = (value?: string | null): string => {
    const tag = asDay(value ?? undefined);
    if (!tag || !/^\d{4}-\d{2}-\d{2}$/.test(tag)) return 'ohne Datum';
    const [jahr, monat, t] = tag.split('-');
    return `${t}.${monat}.${jahr}`;
};

/** '09:30:00' → '09:30'. Siehe IntegrityReportRow.inspectionTime. */
const hhmm = (value?: string | null): string => (value || '').slice(0, 5);

/**
 * Ein ISO-Zeitstempel als 'TT.MM.JJJJ HH:MM:SS' in Ortszeit Europe/Berlin.
 *
 * Sekundengenau, weil die Zwillinge einer Doppelbuchung 50 bis 550 ms
 * auseinanderliegen und sonst nichts sie unterscheidet. Ortszeit und nicht
 * UTC, weil der Rücknahme-Dialog die Ortszeit zeigt: wer dort nach der
 * Uhrzeit aus dem Befund sucht, muss dieselbe Zahl sehen. Die Zone steht
 * ausdrücklich da und haengt nicht an der Maschine, damit der Nachtlauf auf
 * einem UTC-Server dieselbe Zeile schreibt wie der Browser.
 */
const BERLIN_ZEIT = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
});
const formatBookedAt = (value?: string | null): string => {
    const t = Date.parse(value || '');
    if (!Number.isFinite(t)) return 'ohne Buchungszeit';
    // 'TT.MM.JJJJ, HH:MM:SS' → ohne das Komma.
    return BERLIN_ZEIT.format(new Date(t)).replace(',', '');
};

/** Ein Nettobetrag in Euro, deutsch formatiert. */
const euro = (betrag: number): string =>
    `${betrag.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`;

// ═══════════════════════════════════════════════════════════════════════════
// Ist der betroffene Zeitraum schon abgerechnet?
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Abrechnungslage eines einzelnen Tages.
 *
 * REIN: die Zeiträume werden hereingereicht (`buildProjectCheckInput` liefert
 * sie ohnehin je Projekt), es wird nichts nachgeladen und nichts nachgerechnet.
 *
 * Die Regeln, und warum sie so lauten:
 *
 *   • Ohne Tag oder ohne Zeiträume: `unbekannt`. `kein_beleg` wird NIE geraten —
 *     das ist die Entwarnung, und eine falsche Entwarnung kostet hier mehr als
 *     ein „weiß nicht": der Bearbeiter überspringt eine Zeile, hinter der ein
 *     ausgestellter Beleg steht.
 *
 *   • Ein Beleg deckt den Tag, wenn `bis` gesetzt ist und der Tag nicht dahinter
 *     liegt — und, falls `von` gesetzt ist, nicht davor.
 *
 *   • Ein Beleg OHNE `bis` deckt nichts: ohne Ende ist der Zeitraum nicht
 *     auswertbar. Er macht die Lage aber auch nicht zu `kein_beleg` (siehe
 *     `unauswertbar` unten) — sonst entstünde aus einem Beleg ohne
 *     Zeitraumangabe ausgerechnet die Entwarnung.
 *
 *   • `von` darf fehlen, `bis` nicht: eine KUMULATIVE Rechnung rechnet vom
 *     Projektbeginn bis zum Stichtag ab, dort zählt allein das ENDE des
 *     Zeitraums. Das ist im Haus eine dokumentierte Entscheidung (5634: der
 *     Zeitraum liess 2.520 EUR liegen, weil nur das Ende zählt) und keine
 *     Nachsicht dieser Funktion.
 *
 *   • Decken mehrere Belege den Tag, gewinnt der mit dem SPÄTESTEN `bis` — der
 *     jüngste Stand ist der, der den Tag zuletzt beansprucht hat.
 *
 * Verglichen wird über `asDay` und nicht über die Rohzeichenketten: ein
 * Zeitstempel ('2026-07-31T22:00:00Z') gegen einen reinen Tag ('2026-07-31')
 * ist als Zeichenkette GRÖSSER, und genau daran sind im Projekt schon einmal
 * die Leistungen am Monatsletzten aus jedem Zeitraum herausgefallen.
 */
export const abrechnungslageAmTag = (
    tag: string | undefined,
    perioden: IssuedPeriod[],
): Abrechnungslage => {
    const stichtag = asDay(tag ?? undefined);
    if (!stichtag) return { stand: 'unbekannt' };
    if (!perioden || perioden.length === 0) return { stand: 'unbekannt', stichtag };

    let bester: IssuedPeriod | undefined;
    let bestesBis: string | undefined;
    // Ein Beleg, der einen Zeitraum beansprucht, ihn aber nicht benennt, sagt
    // über diesen Tag NICHTS. Deckt kein anderer, bleibt es deshalb bei
    // „unbekannt" statt bei einer erfundenen Entwarnung.
    let unauswertbar = false;

    for (const p of perioden) {
        const bis = asDay(p?.to ?? undefined);
        if (!bis) {
            unauswertbar = true;
            continue;
        }
        if (stichtag > bis) continue;
        const von = asDay(p?.from ?? undefined);
        if (von && stichtag < von) continue;
        if (!bestesBis || bis > bestesBis) {
            bester = p;
            bestesBis = bis;
        }
    }

    if (bester) {
        return {
            stand: 'abgerechnet',
            beleg: bester.invoiceNumber,
            von: bester.from,
            bis: bester.to,
            stichtag,
        };
    }
    return { stand: unauswertbar ? 'unbekannt' : 'kein_beleg', stichtag };
};

/**
 * Ist die Zusatzmenge eines Zwillings schon abgerechnet? Dann ist er nur noch
 * per Storno zu bereinigen — und dafür gibt es keinen Befund (Betreiber
 * 02.10.2026: „die abgerechneten sollen auch nirgends mehr als Warnung
 * auftauchen"). Gilt für `doppelte_buchung` und `tageszwilling_ohne_bericht`.
 *
 * Abgerechnet ist er, wenn EINES zutrifft:
 *
 *   • Ein ausgestellter Beleg deckt den Tag (`abrechnung.stand ===
 *     'abgerechnet'`).
 *
 *   • Der Zählerstand: die Rücknahme der Zusatzzeilen senkte den Zähler unter
 *     abgerechnet + verzichtet. Dann steckt die Zusatzmenge zumindest
 *     teilweise im abgerechneten Stand, und die App wiese die Rücknahme ab —
 *     dieselbe Schranke wie `planReversal` („nie unter abgerechnet", Verzicht
 *     zuerst aufheben). Der Zähler greift auch dort, wo die Zeiträume fehlen
 *     (`unbekannt`) oder ein Beleg keinen auswertbaren Zeitraum trägt.
 *
 * Um wie viel der Zähler sinkt, rechnet `planReversal` aus dem Protokoll,
 * nicht aus der Zusatzmenge: nur um das, was er nach der Entfernung noch über
 * dem Protokoll stünde (Vorfall 6051 #135 — der Versand schrieb den Zwilling,
 * der Zähler stieg nur einmal). Hatte der Zähler die Zusatzzeilen nie
 * gezählt, sinkt er nicht, die Rücknahme geht durch, und der Befund bleibt
 * (Codex-Review 02.10.2026, P2: pauschal `zuViel` abzuziehen hätte einen
 * behebbaren Zwilling verschwiegen). Gezählt wird mit `loggedQuantity`, wie
 * dort.
 *
 * Geprüft wird EINE Rücknahme, die der kleinsten Zusatzzeile: die App nimmt
 * Zeilen einzeln zurück, und die kleinste senkt den Zähler am wenigsten. Geht
 * sie durch, ist der Befund behebbar und bleibt — auch wenn eine zweite
 * Zusatzzeile danach gesperrt wäre (Codex-Review 02.10.2026, Runde 2). Ist
 * schon sie gesperrt, sind es alle.
 *
 * Der Verzicht kommt über `waivedQuantity` und wird nicht nachgerechnet: zwei
 * unabhängig gebildete Verzichtsmengen sind im Repo schon auseinandergelaufen.
 *
 * Die Folge, bewusst in Kauf genommen: ein Zwilling, der erst NACH der
 * Rechnung auffällt, bleibt still. Vor der Rechnung meldet der Nachtlauf ihn
 * täglich als `kein_beleg` — dort ist er noch per Rücknahme zu beheben.
 * Das Fix-SQL der 11 offenen Tageszwillinge zieht dieselbe Schranke; wer sie
 * hier ändert, ändert sie dort mit.
 */
const zwillingSchonAbgerechnet = (
    service: ServiceItem,
    mengen: number[],
    abrechnung: Abrechnungslage,
    serviceLog: ServiceTransaction[],
): boolean => {
    if (abrechnung.stand === 'abgerechnet') return true;
    // Die Zusatzzeilen: alle bis auf EINE größte (dieselbe Lesart wie
    // `zuViel`). Ohne Menge keine Rücknahme und kein Geld — nicht verschweigen.
    const zusatz = [...mengen].sort((a, b) => a - b).slice(0, -1).filter((m) => m > EPS);
    if (zusatz.length === 0) return false;
    return ruecknahmeGesperrt(service, zusatz[0], serviceLog);
};

/**
 * Wiese die App die Rücknahme EINER Zeile mit `menge` auf `service` ab? Die
 * Schranke aus `zwillingSchonAbgerechnet` (dort begründet): der Zähler sinkt
 * nur um das, was er über dem Restprotokoll steht, und nie unter abgerechnet +
 * verzichtet. Auch `bericht_auf_zwei_positionen` fragt sie — je Zeile.
 */
const ruecknahmeGesperrt = (
    service: ServiceItem,
    menge: number,
    serviceLog: ServiceTransaction[],
): boolean => {
    const used = service.quantityUsed || 0;
    const billed = service.quantityBilled || 0;
    const waived = waivedQuantity(service);
    const protokollDanach = loggedQuantity(serviceLog, service.id) - menge;
    const zaehlerSinkt = Math.min(menge, Math.max(0, used - protokollDanach));
    return used - zaehlerSinkt < billed + waived - EPS;
};

/**
 * Der Tag, an dem eine LEISTUNG zuletzt erbracht wurde.
 *
 * Buchungsvorgänge sind ausgeschlossen (`isPostingEntry`): sie tragen das Datum
 * der Abrechnung und nicht das der Arbeit. Wer sie mitnimmt, prüft den Tag der
 * Rechnung gegen den Zeitraum derselben Rechnung — und bekommt reihenweise
 * „abgerechnet" zurück, ohne dass die Leistung selbst je in einem Beleg stand.
 */
const letzterLeistungstag = (
    log: ServiceTransaction[] | undefined,
    serviceId: string,
): string | undefined => {
    let letzter: string | undefined;
    for (const t of log || []) {
        if (t?.serviceId !== serviceId) continue;
        if (isPostingEntry(t)) continue;
        const tag = asDay(t?.date);
        if (!tag) continue;
        if (!letzter || tag > letzter) letzter = tag;
    }
    return letzter;
};

/** Der jüngste Tag im Berichtsarchiv — der Stichtag der berichtsbezogenen Regeln. */
const letzterBerichtstag = (reports: IntegrityReportRow[] | undefined): string | undefined => {
    let letzter: string | undefined;
    for (const r of reports || []) {
        const tag = asDay(r?.date);
        if (!tag) continue;
        if (!letzter || tag > letzter) letzter = tag;
    }
    return letzter;
};

/**
 * Der Fingerabdruck: der Zahlenstand des Befundes.
 *
 * Nach Schlüssel SORTIERT und auf 3 Nachkommastellen gerundet, damit er
 * ausschließlich von den Zahlen abhängt und nicht davon, in welcher
 * Reihenfolge die Eingaben geladen wurden. Ein Fingerabdruck, der sich beim
 * bloßen Umsortieren einer Liste ändert, ließe jeden quittierten Befund beim
 * nächsten Lauf wieder aufleben — und niemand würde die Liste zweimal ansehen.
 *
 * Aus demselben Grund gehört die ABRECHNUNGSLAGE (`details.abrechnung`) nicht
 * hierher: eine neue Rechnung verschlechtert keine Abweichung. Ginge sie ein,
 * lebte jeder quittierte Befund beim nächsten Beleg wieder auf.
 */
export const fingerprintOf = (values: Record<string, number | null | undefined>): string => {
    const teile = Object.keys(values)
        .filter((k) => typeof values[k] === 'number' && Number.isFinite(values[k] as number))
        .sort()
        .map((k) => `${k}=${round3(values[k] as number)}`);
    // Befunde ohne Zahlen (kein Kunde am Projekt, fehlende Startunterlagen)
    // haben keinen Stand, der wachsen könnte. Sie bekommen einen festen
    // Abdruck statt einer leeren Zeichenkette: die Spalte ist not null, und
    // „leer" liest sich wie ein Fehler statt wie „nichts zu messen".
    return teile.length ? teile.join(';') : '-';
};

/** Stabil über Läufe hinweg — daran hängt, dass eine Quittierung morgen noch gilt. */
export const findingKeyOf = (projectId: string, ruleId: string, scopeId: string): string =>
    `${projectId}|${ruleId}|${scopeId}`;

interface ProjectLabels {
    projectId: string;
    projectNumber?: string;
    projectName?: string;
    /** `projects.status`. Steuert, ob ueber dieses Projekt ueberhaupt gemeldet wird. */
    projectStatus?: string | null;
    /**
     * `linkedProjectConfig` — nur bei einer TOCHTER gesetzt. Entscheidet, ob
     * eine Begehungsbuchung der Mutter am selben Tag die Leistung der Tochter
     * deckt (Tagespauschale) oder nicht (Einzelabrechnung).
     *
     * Die GANZE Konfiguration und nicht nur der aktuelle Modus: seit die
     * Abrechnungsart im laufenden Projekt umstellbar ist, gilt fuer jede
     * Begehung die Art IHRES Tages (`billingModeAt`).
     */
    linkedConfig?: LinkedProjectConfig | null;
}


const projectLabel = (ctx: ProjectLabels): string =>
    [ctx.projectNumber, ctx.projectName].filter(Boolean).join(' — ') || ctx.projectId;

/** Die Position, wie sie im Leistungsverzeichnis steht (`title`, nicht `description`). */
const serviceLabel = (service?: ServiceItem, lvPosition?: string): string | undefined => {
    if (service) return `LV ${service.lvPosition || '—'} — ${service.title || 'ohne Titel'}`;
    if (lvPosition) return `LV ${lvPosition}`;
    return undefined;
};

const makeFinding = (
    ctx: ProjectLabels,
    parts: {
        ruleId: string;
        severity: IntegritySeverity;
        scopeKind: IntegrityScopeKind;
        scopeId: string;
        scopeLabel: string;
        message: string;
        expected?: number;
        actual?: number;
        details?: Record<string, unknown>;
        fingerprint: string;
    },
): IntegrityFinding => ({
    findingKey: findingKeyOf(ctx.projectId, parts.ruleId, parts.scopeId),
    projectId: ctx.projectId,
    projectNumber: ctx.projectNumber,
    projectName: ctx.projectName,
    ...parts,
});

// ═══════════════════════════════════════════════════════════════════════════
// Eingaben
// ═══════════════════════════════════════════════════════════════════════════

export interface IntegrityProjectCheckContext extends ProjectLabels {
    services: ServiceItem[];
    reports: IntegrityReportRow[];
    /**
     * Das Leistungsprotokoll. Optional, weil es die REGELN hier nicht braucht —
     * gebraucht wird es für den Stichtag der leistungsbezogenen Befunde (der
     * jüngste Protokolleintrag der Position). Fehlt es, steht dort `unbekannt`.
     */
    serviceLog?: ServiceTransaction[];
    /**
     * Leistungszeiträume der bereits ausgestellten Rechnungen dieses Projekts,
     * unverändert aus `buildProjectCheckInput`. Fehlen sie, ist die
     * Abrechnungslage jedes Befundes `unbekannt` — nie `kein_beleg`.
     */
    issuedPeriods?: IssuedPeriod[];
}

/** Eine erfolgreiche Berichtsversendung aus dem Sendeverlauf (`email_logs`). */
export interface IntegritySentReportMail {
    /** Die Berichtsnummer, gelesen aus dem Betreff (`Nr. N` / `No. N`). */
    inspectionNumber: number;
    /** `email_logs.sent_at`, ISO. */
    sentAt: string;
}

export interface IntegrityExtraRulesContext extends IntegrityProjectCheckContext {
    serviceLog: ServiceTransaction[];
    /**
     * Die erfolgreichen Berichtsversendungen dieses Projekts. Fehlt die Liste,
     * schweigt `versendet_ohne_archiv` — ohne Sendeverlauf ist nichts zu sagen.
     */
    sentReportMails?: IntegritySentReportMail[];
    /**
     * Der laufende Bericht (`metadata.inspectionNumber`, `.date`,
     * `.sectionId`). Ohne Nummer oder Begehungstag schweigt
     * `versendet_ohne_archiv`. Die Nummer darf als Text kommen („149"), der
     * Bestand kennt beides.
     */
    currentReport?: {
        inspectionNumber?: number | string | null;
        date?: string | null;
        sectionId?: string | null;
    } | null;
}

export interface IntegrityProjectContext extends IntegrityExtraRulesContext {
    /**
     * Das Ergebnis von `runProjectCheck`. Fehlt es, meldet der Lauf für dieses
     * Projekt nur die vier Zusatzregeln — er behauptet dann bewusst nicht,
     * das Projekt sei geprüft.
     */
    check?: ProjectCheckResult;
}

export type { IntegrityReportRow } from './integrityTypes';

// ═══════════════════════════════════════════════════════════════════════════
// 1. Der Projektcheck wird zu Befunden
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Ampel des Projektchecks in die Schwere des Wächters.
 *
 * Rot heißt dort „Ausstellen gesperrt" — das ist die härteste Aussage, die
 * das Haus über ein Projekt trifft, und sie bleibt hier kritisch.
 */
const severityFromTraffic = (finding: ProjectCheckFinding): IntegritySeverity => {
    if (finding.traffic === 'red') return 'kritisch';
    if (finding.traffic === 'yellow') return 'warnung';
    return 'hinweis';
};

/**
 * Regeln, die das BERICHTSARCHIV gegen den Begehungszähler halten. Für sie
 * gehört der Archivstand in die Details, sonst muss der Bearbeiter ihn im
 * Projekt nachzählen, bevor er den Befund überhaupt einordnen kann.
 */
const REPORT_RULES = new Set(['report_count_mismatch', 'inspection_counter_mismatch']);

/**
 * Befunde des Projektchecks, die KEIN Integritaetsbefund sind.
 *
 * Die Trennlinie: der Projektcheck fragt "darf ich JETZT eine Rechnung
 * ausstellen?", der Waechter fragt "widersprechen sich die Daten?". Vier
 * Regeln beantworten ausschliesslich die erste Frage. Sie beschreiben einen
 * ZUSTAND, keinen Widerspruch — und ein Zustand, der sich nie aufloest, ist
 * als Dauerbefund wertlos. Gemessen am Bestand (Lauf vom 30.08.2026, 538
 * Projekte, 1.564 Befunde) waeren es 445 Zeilen gewesen, davon 216 rot: ein
 * Drittel der Liste als Rauschen, und nach zwei Wochen schaut niemand mehr
 * hinein.
 *
 * Im Einzelnen:
 *   `no_open_positions`  — "nichts zu fakturieren". Fuer ein fertig
 *                          abgerechnetes Projekt der gesunde Endzustand.
 *   `contract_overrun`   — mehr abgerechnet als beauftragt. Mehrmengen SIND
 *                          abrechenbar (Entscheidung 2026-08-11, in
 *                          projectCheckLogic ausdruecklich "NIE Sperre").
 *                          121 Treffer im Bestand, kein einziger ein Fehler.
 *   `customer_missing`   — kein Rechnungsempfaenger. Bei internen und
 *                          abgeschlossenen Projekten normal; stand als
 *                          KRITISCH in der Liste (108 Treffer).
 *   `missing_start_docs` — Vorankuendigung/Unterlage fehlt. Berechtigt, aber
 *                          das ist Compliance vor der Rechnung und gehoert
 *                          dorthin, wo sie ohnehin geprueft wird (108).
 *
 * Alle vier bleiben im Projektcheck unveraendert wirksam — sie werden hier
 * nur nicht ERINNERT. Wer eine Rechnung schreibt, sieht sie weiterhin.
 *
 * Dazu zwei Hinweiszeilen der Paketpositionen (docs/positionsbestandteile-
 * plan.md, Controller-Entscheid 19.10.2026), aus demselben Grund — Zustand,
 * kein Widerspruch:
 *   `position_gebucht_bestandteile_offen` — gebucht, aber Bestandteile offen.
 *                          Ein Arbeitsstand; das Gate haelt das Geld zurueck.
 *   `bestandteil_doppelt_als_position`    — Unterlage im Paket UND als eigene
 *                          Position. Zulaessig (E9), aendert sich nie von
 *                          selbst — als Dauerbefund reines Rauschen.
 * `bestandteile_vollstaendig_nicht_gebucht` bleibt BEWUSST drin: dort fehlt
 * Geld, und der Befund ist das Netz, falls weder die ausloesende Handlung noch
 * der Nachtlauf gebucht hat.
 */
const NICHT_UEBERNOMMEN = new Set([
    'no_open_positions',
    'contract_overrun',
    'customer_missing',
    'missing_start_docs',
    'position_gebucht_bestandteile_offen',
    'bestandteil_doppelt_als_position',
]);

/**
 * Regeln, deren Abweichung NICHT in `expected`/`actual` steht, sondern nur im
 * Text des Befundes.
 *
 * Ohne sie bliebe der Fingerabdruck dieser Regeln konstant, und eine
 * Quittierung waere ein Dauerdeckel: der Begehungszaehler darf von 5 auf 9
 * laufen, ohne dass der Befund je wieder auflebt — ausgerechnet bei einer der
 * haeufigsten Driftquellen.
 *
 * Bewusst eine kurze Liste und keine allgemeine Regel: `stale_open_position`
 * traegt "seither X Monate" im Text und lebte sonst jeden Monat neu auf.
 */
const TEXT_FINGERPRINT_RULES = new Set(['inspection_counter_mismatch']);

/** Ein Projektcheck-Ergebnis wird zu Befunden. */
export const findingsFromProjectCheck = (
    ctx: IntegrityProjectCheckContext,
    result: ProjectCheckResult,
): IntegrityFinding[] =>
    (result?.findings || []).filter((f) => !NICHT_UEBERNOMMEN.has(f.rule)).map((f) => {
        const service = f.serviceId ? (ctx.services || []).find((s) => s.id === f.serviceId) : undefined;
        // Trägt der Befund eine Position, hängt er an ihr — sonst am Projekt.
        // Der Sprung aus der Liste führt danach an die Stelle, an der repariert
        // wird, und nicht bloß ins Projekt.
        const scopeKind: IntegrityScopeKind = f.serviceId ? 'service' : 'projekt';
        const scopeId = f.serviceId || ctx.projectId;
        const details: Record<string, unknown> = {
            rule: f.rule,
            traffic: f.traffic,
            checkSeverity: f.severity,
            acknowledgeable: f.acknowledgeable,
        };
        const lvPosition = f.lvPosition || service?.lvPosition;
        if (lvPosition) details.lvPosition = lvPosition;
        if (REPORT_RULES.has(f.rule)) details.archivierteBerichte = (ctx.reports || []).length;
        // Die Abrechnungslage. Sie steht in den Details und geht NICHT in den
        // Fingerabdruck ein (siehe unten) — sie beschreibt die Umgebung des
        // Befundes, nicht seine Abweichung.
        //
        // Der Stichtag: für die Regel, die das Berichtsarchiv gegen den Zähler
        // hält, der jüngste Berichtstag; für eine Position der Tag ihrer letzten
        // erbrachten Leistung. Ein Befund, der weder an einem Bericht noch an
        // einer Position hängt (Projektebene), hat keinen Tag und bleibt
        // `unbekannt` — ein erfundener Stichtag wäre eine erfundene Auskunft.
        details.abrechnung = abrechnungslageAmTag(
            f.rule === 'report_count_mismatch'
                ? letzterBerichtstag(ctx.reports)
                : f.serviceId
                    ? letzterLeistungstag(ctx.serviceLog, f.serviceId)
                    : undefined,
            ctx.issuedPeriods || [],
        );
        return makeFinding(ctx, {
            ruleId: f.rule,
            severity: severityFromTraffic(f),
            scopeKind,
            scopeId,
            scopeLabel:
                serviceLabel(service, f.lvPosition) ||
                (scopeKind === 'projekt' ? `Projekt ${projectLabel(ctx)}` : scopeId),
            message: `${f.title} — ${f.detail}`,
            expected: f.expected,
            actual: f.actual,
            details,
            fingerprint: TEXT_FINGERPRINT_RULES.has(f.rule)
                ? `${fingerprintOf({ expected: f.expected, actual: f.actual })}|${f.detail ?? ''}`
                : fingerprintOf({ expected: f.expected, actual: f.actual }),
        });
    });

// ═══════════════════════════════════════════════════════════════════════════
// 2. Versendet ohne Buchung
// ═══════════════════════════════════════════════════════════════════════════

export const UNBOOKED_RULE_ID = 'versendet_ohne_buchung';

/**
 * Zeilen der Sicht `public.v_unbooked_sent_reports` werden zu Befunden.
 *
 * Die Lage wird NICHT nachgerechnet: die Sicht kennt den Mutter/Tochter-Zweig,
 * die Betreffzuordnung des Sendeverlaufs und den Ausschluss der reinen
 * Zusatzleistungszeilen (`isInspectionBilled`). Der Altbestand trägt kein
 * `reportRef` — wer das im Client nachbaut, verliert genau diese Zeilen.
 *
 * Zeilen einer TOCHTER bleiben bewusst drin, obwohl der Wächter Tochter-
 * projekte sonst überspringt: hier ist die Aussage nicht „diese Mengen sind
 * offen", sondern „dieser Bericht ist beim Kunden und nirgends gebucht". Die
 * Sicht sagt über `parent_project_id` dazu, wo gebucht werden muss.
 */
export const findingsFromUnbookedReports = (
    rows: UnbookedSentReportRow[],
    /**
     * Leistungszeiträume je Projekt, für die Abrechnungslage.
     *
     * Die Sicht liefert sie nicht mit — sie kommen aus den Projekten, die der
     * Lauf geladen hat. Ein Projekt, das nicht darin steht (Tochterprojekt,
     * nicht ladbar, Lauf ohne Projektteil), bekommt eine LEERE Liste und damit
     * `unbekannt`. Genau so soll es sein: hier nichts zu wissen ist ehrlich,
     * „kein Beleg" wäre geraten.
     */
    periodenJeProjekt: Map<string, IssuedPeriod[]> = new Map(),
    /**
     * Projekte, die nicht mehr melden — im Archiv UND ohne offene Forderung.
     *
     * Als MENGE und nicht als Statusvergleich: ob nichts mehr abzurechnen ist,
     * steht in den Positionen, und die fuehrt die Sicht nicht mit. Ein Projekt,
     * das dieser Lauf nicht geladen hat, fehlt in der Menge und meldet
     * weiter — die sichere Richtung.
     */
    stilleProjekte: Set<string> = new Set(),
    /**
     * `<projectId>:<inspectionNumber>` je Tochterbericht, dessen Begehungstag
     * im Protokoll der Mutter bereits gebucht ist (Tagespauschale).
     */
    tagGedeckt: Set<string> = new Set(),
): IntegrityFinding[] =>
    (rows || [])
        .filter((row) => !stilleProjekte.has(row.project_id))
        .filter((row) => !tagGedeckt.has(`${row.project_id}:${row.inspection_number}`))
        .map((row) => {
        const nummer = row.inspection_number ?? 0;
        const hinweise = [
            `Bericht #${nummer} ist am ${formatDay(row.first_sent_at)} versendet worden, aber die Begehung ist nicht gebucht.`,
        ];
        if (row.nur_zusatzleistung_gebucht) {
            hinweise.push('Gebucht wurde nur eine Zusatzleistung — die Begehung selbst ist nicht abgerechnet.');
        }
        if (row.parent_project_id) hinweise.push('Die Abrechnung liegt beim Mutterprojekt.');
        return makeFinding(
            {
                projectId: row.project_id,
                projectNumber: row.project_number || undefined,
                projectName: row.project_name || undefined,
            },
            {
                ruleId: UNBOOKED_RULE_ID,
                // Ab Tag 0 ein Mangel und keine Fälligkeit: gebucht wird beim
                // Versand. Ist beim Versand nichts gebucht worden, passiert es
                // von allein nie mehr (unbookedReportsLogic.unbookedPriority).
                severity: 'kritisch',
                scopeKind: 'report',
                scopeId: row.report_history_id,
                scopeLabel: `Bericht #${nummer} vom ${formatDay(row.inspection_date)}`,
                message: hinweise.join(' '),
                expected: 1,
                actual: 0,
                details: {
                    // Nie zusammengesetzt — eine Stelle, ein Format.
                    reportRef: reportRefOf(nummer, row.section_id),
                    inspectionNumber: nummer,
                    sectionId: row.section_id,
                    firstSentAt: row.first_sent_at,
                    sendCount: row.send_count,
                    daysSinceSend: row.days_since_send,
                    nurZusatzleistungGebucht: row.nur_zusatzleistung_gebucht,
                    parentProjectId: row.parent_project_id,
                    reportExempt: row.report_exempt,
                    // Stichtag ist das BEGEHUNGSdatum und nicht der Versandtag:
                    // gebucht und abgerechnet wird die Begehung, und ihr Tag
                    // entscheidet, in welchen Leistungszeitraum sie fällt.
                    abrechnung: abrechnungslageAmTag(
                        row.inspection_date ?? undefined,
                        periodenJeProjekt.get(row.project_id) || [],
                    ),
                },
                // Die Zahl der Versendungen, NICHT das Alter: der Befund darf
                // nicht jede Nacht wieder aufleben, nur weil ein Tag vergangen
                // ist. Ein erneuter Versand dagegen ist ein neuer Vorgang und
                // gehört erneut angesehen.
                fingerprint: fingerprintOf({ expected: 1, actual: 0, versendungen: row.send_count }),
            },
        );
    });

// ═══════════════════════════════════════════════════════════════════════════
// 3. Die sieben Regeln, die es noch nicht gibt
// ═══════════════════════════════════════════════════════════════════════════

export const DUPLICATE_ARCHIVE_RULE_ID = 'doppelte_archivzeile';
export const DUPLICATE_BOOKING_RULE_ID = 'doppelte_buchung';
export const REPORT_TWO_POSITIONS_RULE_ID = 'bericht_auf_zwei_positionen';
export const DAY_TWIN_RULE_ID = 'tageszwilling_ohne_bericht';

/**
 * Wie weit die Buchungszeiten einer AUFTEILUNG höchstens auseinanderliegen.
 * Ein Buchungslauf schreibt alle Zeilen in Millisekunden; die Doppelbuchung
 * von 6051 #149 lag eineinhalb Stunden auseinander.
 */
const AUFTEILUNG_FENSTER_MS = 2 * 60 * 1000;
export const ORPHAN_ENTRY_RULE_ID = 'eintrag_ohne_leistung';
export const GEBUCHT_OHNE_ARCHIV_RULE_ID = 'gebucht_ohne_archiv';
export const VERSENDET_OHNE_ARCHIV_RULE_ID = 'versendet_ohne_archiv';

/**
 * Der Abschnitt eines Berichts als Schlüssel.
 *
 * `null` und `'all'` sind DIESELBE Lage: das Projekt ohne Abschnittsgliederung.
 * `report_history.section_id` schreibt dafür NULL, `reportRefOf` schreibt
 * 'all'. Ohne diese eine Übersetzung verglichen die beiden Seiten aneinander
 * vorbei und jeder Bericht eines nicht gegliederten Projekts wäre ein Befund.
 */
const abschnittKey = (sectionId?: string | null): string => sectionId || 'all';

interface Hauptbuchung {
    nr: number;
    sectionId: string | null;
    /** Trägt mindestens eine der Zeilen einen `reportRef`? */
    hatRef: boolean;
    /** Der Begehungstag, wie ihn die Buchung festgehalten hat. */
    date?: string;
    entryIds: string[];
}

/**
 * Die Hauptbuchungen im Protokoll, je (Abschnitt, Berichtsnummer).
 *
 * Erkannt wird über beide Wege, die `bookedReportNumbers` kennt — Kennung und
 * Beschreibung —, aber unter Mitnahme des ABSCHNITTS: `bookedReportNumbers`
 * liefert nur die Nummern, und in einem gegliederten Projekt gibt es die
 * Nummer 7 in jedem Abschnitt einmal.
 *
 * Zwei Zeilen sind ausdrücklich KEINE erbrachte Begehung und bleiben draußen:
 *
 *  - Buchungsvorgänge (`isPostingEntry`: 'BU-', 'VZ-', 'RN-', 'MB-' und die
 *    manuelle Verbuchung). Sie tragen den `reportRef` mit, sind aber die
 *    ABRECHNUNG der Begehung und nicht die Begehung.
 *  - `RPT-CHILD-…`: die Buchung einer TOCHTERbegehung im Protokoll der Mutter.
 *    Ihre Archivzeile liegt im Tochterprojekt, nicht hier — hier gesucht,
 *    fehlte sie immer.
 */
const hauptbuchungenJeBericht = (
    serviceLog: ServiceTransaction[] | undefined,
): Map<string, Hauptbuchung> => {
    const out = new Map<string, Hauptbuchung>();
    for (const eintrag of serviceLog || []) {
        if (!eintrag) continue;
        if (isPostingEntry(eintrag)) continue;

        const ref = String(eintrag.reportRef || '');
        if (ref.startsWith('RPT-CHILD-')) continue;

        let nr: number | undefined;
        let sectionId: string | null = null;
        let hatRef = false;

        // `RPT-<abschnitt>-<nummer>`, gebildet von `reportRefOf`. Der Abschnitt
        // ist eine UUID und enthält selbst Bindestriche — deshalb greift die
        // Nummer von RECHTS und der Rest ist der Abschnitt.
        const ausRef = /^RPT-(.+)-(\d+)$/.exec(ref);
        if (ausRef) {
            nr = Number(ausRef[2]);
            sectionId = ausRef[1] === 'all' ? null : ausRef[1];
            hatRef = true;
        } else {
            const ausText = /^Bericht #(\d+) abgeschlossen$/.exec(String(eintrag.description || ''));
            if (ausText) nr = Number(ausText[1]);
        }
        if (!nr || !Number.isFinite(nr)) continue;

        const key = `${abschnittKey(sectionId)}-${nr}`;
        const bestand = out.get(key);
        if (bestand) {
            bestand.hatRef = bestand.hatRef || hatRef;
            if (!bestand.date) bestand.date = eintrag.date;
            if (eintrag.id) bestand.entryIds.push(eintrag.id);
        } else {
            out.set(key, {
                nr,
                sectionId,
                hatRef,
                date: eintrag.date,
                entryIds: eintrag.id ? [eintrag.id] : [],
            });
        }
    }
    return out;
};

/**
 * Die Menge, mit der eine Protokollzeile zählt.
 *
 * Bei einer ALT-Zeile (eingelesene Alt-Rechnung) ist `quantity` die BELEG-
 * menge — die Aussage „diese Rechnung hat N abgegolten". Angewandt wurde davon
 * oft weniger, denn beim Einlesen wird der Zähler ANGEHOBEN und nicht addiert
 * (externalAdvances.nextBilled). Mit der Rohmenge schlüge hier jede Summe bei
 * jedem Projekt mit Alt-Import an.
 */
const effectiveQuantity = (entry: ServiceTransaction): number => {
    const roh = Number(entry?.quantity) || 0;
    if (!isBackfilledAltEntry(entry)) return roh;
    return Number(entry.appliedUsed ?? roh) || 0;
};

/**
 * Die Berichtsnummer einer Zeile als Schlüssel — für `tageszwilling_ohne_bericht`.
 *
 * Die UNTERnummern der Altbestände kommen ZUERST („Begehung Nr. 42.1",
 * „Bericht Nr. 29.2" — 6172, 6051): `reportNumberOf` liest daraus die 42,
 * und die wäre hier ein falscher Zwilling von „Bericht Nr. 42". Für die
 * Frage „nennen die Zeilen ZWEI Berichte?" ist die Unternummer gerade die
 * Antwort — deshalb als Text, nie als Zahl. Danach wie überall
 * `reportNumberOf` (Ref, „Bericht #N abgeschlossen", „#N", „Nr. N").
 */
const berichtsSchluessel = (t: ServiceTransaction): string | null => {
    const unter = /(?:#|\bNr\.?\s*)(\d{1,9}\.\d{1,3})(?![0-9])/.exec(String(t.description || ''));
    if (unter) return unter[1];
    const nr = reportNumberOf(t);
    return nr === null ? null : String(nr);
};

/** Die sieben Zusatzregeln. */
export const findingsFromExtraRules = (ctx: IntegrityExtraRulesContext): IntegrityFinding[] => {
    const out: IntegrityFinding[] = [];

    // ── Doppelte Archivzeile ─────────────────────────────────────────────
    //
    // Vorfall 6164: zwei Zeilen mit identischem Datum UND identischer Uhrzeit
    // ließen die Tagesregel des Leistungsnachweises eine bezahlte Leistung
    // nullen. Die Tagesregel bleibt, wie sie ist — korrigiert werden die
    // Daten, nicht die Regel; deshalb ist das hier ein Befund und keine
    // Nachsicht im Nachweis.
    //
    // Der Gleichheitsbegriff ist wortgleich der des Unique-Index
    // (report_history_unique.sql): NULL im Abschnitt zählt als GLEICH
    // (`nulls not distinct`), sonst bliebe ausgerechnet der Normalfall
    // „Projekt ohne Abschnittsgliederung" ungeprüft.
    const gruppen = new Map<string, IntegrityReportRow[]>();
    for (const r of ctx.reports || []) {
        const tag = asDay(r?.date);
        const zeit = hhmm(r?.inspectionTime);
        // Nur wenn BEIDE Zeiten gesetzt sind: ohne Uhrzeit sind zwei Begehungen
        // am selben Tag der Normalfall (Abschnitte, Nachbegehung), und die
        // Meldung wäre in der Fläche Rauschen.
        if (!tag || !zeit) continue;
        const key = `${tag}|${zeit}|${r.sectionId ?? ''}`;
        const liste = gruppen.get(key);
        if (liste) liste.push(r);
        else gruppen.set(key, [r]);
    }
    for (const [key, zeilen] of gruppen) {
        if (zeilen.length < 2) continue;
        const [tag, zeit] = key.split('|');
        // Anker ist die kleinste Archiv-Kennung: kommt später eine DRITTE
        // Zeile dazu, bleibt der Schlüssel derselbe und nur der Fingerabdruck
        // wächst — der Befund lebt wieder auf, statt als neuer daneben zu
        // stehen und den alten als erledigt zurückzulassen.
        const ids = zeilen.map((z) => z.id).sort();
        const nummern = zeilen
            .map((z) => z.inspectionNumber)
            .filter((n): n is number => typeof n === 'number')
            .sort((a, b) => a - b);
        out.push(
            makeFinding(ctx, {
                ruleId: DUPLICATE_ARCHIVE_RULE_ID,
                severity: 'warnung',
                scopeKind: 'report',
                scopeId: ids[0],
                scopeLabel: nummern.length
                    ? `Berichte ${nummern.map((n) => `#${n}`).join(' und ')} vom ${formatDay(tag)} ${zeit}`
                    : `Archivzeilen vom ${formatDay(tag)} ${zeit}`,
                message: `${zeilen.length} Archivzeilen tragen dasselbe Datum (${formatDay(tag)}) und dieselbe Uhrzeit (${zeit}). Der Leistungsnachweis zählt sie als eine Begehung.`,
                expected: 1,
                actual: zeilen.length,
                details: {
                    reportIds: ids,
                    inspectionNumbers: nummern,
                    date: tag,
                    inspectionTime: zeit,
                    sectionId: zeilen[0].sectionId ?? null,
                    // Der Tag der doppelten Zeilen selbst — beide tragen ihn.
                    abrechnung: abrechnungslageAmTag(tag, ctx.issuedPeriods || []),
                },
                fingerprint: fingerprintOf({ expected: 1, actual: zeilen.length }),
            }),
        );
    }

    // ── Doppelte Buchung ─────────────────────────────────────────────────
    //
    // Vorfall vom 02.09.2026 (6110 #25 dreimal, 6185 #22 zweimal, 6051 #135
    // zweimal): derselbe Bericht wurde auf dieselbe Position mehrfach gebucht.
    // Die Zwillinge tragen denselben `reportRef`, denselben Tag und denselben
    // Text — nur `bookedAt` liegt 50 bis 550 ms auseinander (ein Resume-Lauf
    // der Versandkette, 950b071). Jeder Zwilling ist ein Begehungspreis zu
    // viel auf der nächsten Rechnung; der Zähler von 6110 stand auf 25 statt
    // 23. Aufgefallen ist es nur, weil die Drift-Sicht des Journals den
    // zweiten Zwilling als `nur_json` führte — der Riegel dort lässt je
    // (Projekt, Bericht, Position) nur einen durch. Das JSON selbst bewachte
    // niemand, und die Rechnung liest das JSON.
    //
    // Der Leistungsbegriff ist der des Rücknahme-Dialogs (`reversibleEntries`:
    // keine Buchungsvorgänge, Menge > 0), und Zwillinge werden über den
    // BERICHT erkannt (`reportRef`), nie über Datum oder Text — Datum und Text
    // sind bei jeder zweiten Begehung gleich, der Bericht nur bei einer
    // Doppelbuchung. Deshalb erlischt der Befund genau dann, wenn der Dialog
    // keine Zwillinge mehr anbietet: eine Rücknahme entfernt die Zeile und
    // hinterlässt eine 'RN-'-Spur mit Menge 0, die hier nicht zählt.
    //
    // Was bewusst KEIN Befund ist:
    //  - der Kind-Marker `RPT-CHILD-…` mit Menge 0 (Tagespauschale): keine
    //    Leistung, fällt über die Menge heraus. Ein Kind-Ref MIT Menge ist
    //    eine gebuchte Tochterbegehung und zählt wie jede andere.
    //  - Zeilen ohne `reportRef`: ohne Bericht lässt sich Gleichheit nicht
    //    behaupten (Altbestand, Nachträge von Hand, Sammelnachtrag).
    //  - `ALT-`-Zeilen: Bestandsübernahme aus einer Alt-Rechnung, keine
    //    Buchung dieser App.
    //  - eine Zusatzleistung (`Zusatzleistung: Bericht #N`) auf einer ANDEREN
    //    Position als die Hauptbuchung: anderer serviceId, andere Gruppe. Ein
    //    Bericht darf auf mehrere Positionen buchen — nur nicht zweimal auf
    //    dieselbe.
    //  - Zeilen einer gelöschten Position: die meldet `eintrag_ohne_leistung`,
    //    und ohne Position gibt es weder Preis noch Dialog.
    //  - ein Zwilling, dessen Zusatzmenge schon abgerechnet ist
    //    (`zwillingSchonAbgerechnet`, Betreiber 02.10.2026): ihn behebt nur
    //    noch ein Storno, und das will der Betreiber nicht.
    //  - eine manuelle Verbuchung („Manuelle Verbuchung durch Admin",
    //    `isManualPosting`): ein Abrechnungsstempel, keine Leistung — die alte
    //    Form von BU-. `reversibleEntries` lässt sie durch, als Zwilling
    //    gemeldet wäre sie aber eine gebuchte Zeile (Review 02.10.2026: 5847,
    //    6099).
    for (const service of ctx.services || []) {
        if (!service?.id) continue;
        const jeBericht = new Map<string, ServiceTransaction[]>();
        for (const t of reversibleEntries(service, ctx.serviceLog || [])) {
            if (isBackfilledAltEntry(t)) continue;
            if (isManualPosting(t)) continue;
            const ref = String(t.reportRef || '').trim();
            if (!ref) continue;
            const liste = jeBericht.get(ref);
            if (liste) liste.push(t);
            else jeBericht.set(ref, [t]);
        }
        for (const [ref, zeilen] of jeBericht) {
            if (zeilen.length < 2) continue;
            // Reihenfolgeunabhängig: nach Buchungszeit, dann id. Die Ausgabe
            // darf nicht davon abhängen, wie das Protokoll geladen wurde.
            const sortiert = [...zeilen].sort((a, b) =>
                `${a.bookedAt || ''}|${a.id}`.localeCompare(`${b.bookedAt || ''}|${b.id}`),
            );
            const mengen = sortiert.map((t) => Number(t.quantity) || 0);
            // Der größte Eintrag gilt als der richtige, der Rest ist zu viel —
            // bei gleichen Mengen (der Regelfall) ist das (Anzahl − 1) × Menge.
            // Nicht die Summe: eine Begehung ist erbracht und gehört gefordert.
            const zuViel = round3(mengen.reduce((s, m) => s + m, 0) - Math.max(...mengen));
            const preis = Number(service.unitPrice) || 0;
            const preisFehlt = !(preis > 0);
            const betragNetto = Math.round(zuViel * preis * 100) / 100;
            const nr = reportNumberOf(sortiert[0]);
            const tag = asDay(sortiert[0].date);
            const bericht = nr === null ? `Bericht ${ref}` : `Bericht #${nr}`;
            const einheit = service.unit || '';
            const zeiten = sortiert.map((t) => formatBookedAt(t.bookedAt));
            const saetze = [
                `${bericht} ist auf ${serviceLabel(service)} ${sortiert.length}-mal gebucht — ` +
                    `${round3(zuViel)} ${einheit} zu viel.`.replace('  ', ' '),
                preisFehlt
                    // Kein Freispruch: der Preis fehlt, nicht die Forderung.
                    ? 'Die Position trägt keinen Einheitspreis; der Betrag steht deshalb auf 0,00 EUR, nicht weil nichts zu viel gefordert würde.'
                    : `Die nächste Rechnung fordert damit ${euro(betragNetto)} netto zu viel.`,
                `Gebucht ${zeiten.join(', ')} — im Rücknahme-Dialog der Position an der Buchungsuhrzeit zu erkennen.`,
            ];
            // Stichtag ist der Begehungstag der Buchung: er entscheidet, ob
            // der Zwilling schon in einem Beleg steckt oder erst im nächsten
            // stünde.
            const abrechnung = abrechnungslageAmTag(tag, ctx.issuedPeriods || []);
            // Schon abgerechnet: nur noch per Storno zu beheben — kein Befund
            // (Betreiber 02.10.2026, siehe die Funktion).
            if (zwillingSchonAbgerechnet(service, mengen, abrechnung, ctx.serviceLog || [])) continue;
            out.push(
                makeFinding(ctx, {
                    ruleId: DUPLICATE_BOOKING_RULE_ID,
                    // Kritisch, nicht Warnung: anders als die doppelte
                    // Archivzeile ist das keine Frage des Nachweises, sondern
                    // eine falsche Geldforderung beim nächsten Beleg.
                    severity: 'kritisch',
                    scopeKind: 'service',
                    // Position UND Bericht im Schlüssel: kommt ein dritter
                    // Zwilling dazu, bleibt es derselbe Befund und nur der
                    // Fingerabdruck wächst; bleibt eine Zeile übrig, erlischt
                    // er. Ein zweiter Bericht auf derselben Position ist ein
                    // eigener Befund.
                    scopeId: `${service.id}|${ref}`,
                    scopeLabel: `${serviceLabel(service)} · ${bericht}${tag ? ` vom ${formatDay(tag)}` : ''}`,
                    message: saetze.join(' '),
                    expected: 1,
                    actual: sortiert.length,
                    details: {
                        serviceId: service.id,
                        lvPosition: service.lvPosition,
                        title: service.title,
                        unit: einheit,
                        unitPrice: preis,
                        preisFehlt,
                        reportRef: ref,
                        inspectionNumber: nr,
                        date: tag,
                        entryCount: sortiert.length,
                        // Die Zwillinge selbst, in Buchungsreihenfolge — die
                        // ids sind das Einzige, was sie sicher unterscheidet.
                        entryIds: sortiert.map((t) => t.id),
                        bookedAt: sortiert.map((t) => t.bookedAt ?? null),
                        quantities: mengen,
                        zuViel,
                        betragNetto,
                        // Nach der Schranke oben nur noch `unbekannt` oder
                        // `kein_beleg`.
                        abrechnung,
                    },
                    // Die Menge zu viel gehört hinein: ein Zwilling mit anderer
                    // Menge ist eine andere Abweichung. Der Preis nicht — eine
                    // Preisänderung verschlechtert die Lage nicht.
                    fingerprint: fingerprintOf({ expected: 1, actual: sortiert.length, zuViel }),
                }),
            );
        }
    }

    // ── Bericht auf zwei Positionen ──────────────────────────────────────
    //
    // Vorfall 6051 #149 (29.09.2026): der Bericht stand auf 2.7.60
    // Tagespauschale (Fix-SQL, 14:55) UND auf 2.7.20 Halbtagspauschale
    // (Nachhol-Marker auf Rolfs Gerät, 16:31). `doppelte_buchung` gruppiert je
    // Position und schwieg; Riegel 73/74 und die Drift-Sicht hängen ebenfalls
    // an (Position, Bericht). Die September-Rechnung forderte eine Pauschale
    // zu viel — aufgefallen ist es der Buchhaltung, weil der Nachweis
    // (Tagesregel) nur eine zeigte.
    //
    // Schlüssel ist der `reportRef`, wie bei der Schwester — nie Datum oder
    // Text. Gezählt werden Hauptbuchungen (`reversibleEntries`: Menge > 0,
    // keine Buchungsvorgänge) auf mindestens ZWEI Positionen.
    //
    // Was bewusst KEIN Befund ist:
    //  - die AUFTEILUNG auf Lose: EIN Buchungslauf schreibt alle Zeilen, ihre
    //    `bookedAt` liegen Millisekunden auseinander. Liegen alle innerhalb von
    //    `AUFTEILUNG_FENSTER_MS`, schweigt die Regel. Fehlt einer Zeile die
    //    Buchungszeit, ist das nicht zu entscheiden — dann meldet sie, und der
    //    Text sagt, dass eine Aufteilung zu quittieren ist.
    //  - eine Zusatzleistung desselben Berichts auf einer anderen Position:
    //    ein Bericht darf Zuschläge auf beliebigen Positionen tragen.
    //  - Zeilen ohne `reportRef` (Altbestand: 6051 trägt von Februar bis Mai
    //    17 Tage mit „Bericht #N abgeschlossen" auf 2.7.60 und „Bericht Nr. N"
    //    auf 2.7.20 — alle abgerechnet).
    //  - `ALT-`-Zeilen und manuelle Verbuchungen, wie bei der Schwester.
    //  - ein Paar, das schon abgerechnet ist: ein Beleg deckt den Tag, oder
    //    KEINE der Zeilen ließe sich mehr zurücknehmen (`ruecknahmeGesperrt`).
    //    Betreiber 02.10.2026, wie bei den Schwestern.
    //  - Zeilen einer gelöschten Position: die meldet `eintrag_ohne_leistung`.
    //
    // Seit 05.10.2026 sperrt der Buchungslauf diesen Fall
    // (`same_report_other_position` in reportBooking.ts). Die Regel findet den
    // Bestand und alles, was an der App vorbei entsteht (SQL, alte Builds).
    //
    // Für den Schattenvergleich (befundVergleich) rechnet sie aus Journal und
    // JSON dasselbe: der Journal-Riegel lässt je (Bericht, Position) eine Zeile
    // durch, über ZWEI Positionen stehen beide Zeilen auch dort. Schlüssel und
    // Fingerabdruck tragen deshalb keine Kennungen, nur Zählungen.
    {
        const jeRef = new Map<string, Array<{ t: ServiceTransaction; service: ServiceItem }>>();
        for (const service of ctx.services || []) {
            if (!service?.id) continue;
            for (const t of reversibleEntries(service, ctx.serviceLog || [])) {
                if (isBackfilledAltEntry(t)) continue;
                if (isManualPosting(t)) continue;
                if (/^Zusatzleistung\b/i.test(String(t.description || '').trim())) continue;
                const ref = String(t.reportRef || '').trim();
                if (!ref) continue;
                const liste = jeRef.get(ref);
                if (liste) liste.push({ t, service });
                else jeRef.set(ref, [{ t, service }]);
            }
        }
        for (const [ref, roh] of jeRef) {
            if (new Set(roh.map((z) => z.service.id)).size < 2) continue;
            // Je Position nur die FRÜHESTE Zeile. Ein Zwilling auf derselben
            // Position ist der Fall von `doppelte_buchung` — und das Journal
            // führt je (Bericht, Position) ohnehin nur eine Zeile. Zählte er
            // hier mit, zöge eine Aufteilung A+B um 10:00 plus Zwilling A um
            // 10:10 das Fenster auf zehn Minuten: das JSON meldete, das Journal
            // nicht, und der Schattenvergleich sähe eine Abweichung
            // (Codex-Review 05.10.2026).
            // Nach der ZEIT sortiert, nicht nach dem Text: das Journal reicht
            // `booked_at` als '+00:00' durch, das JSON schreibt 'Z'. Ohne
            // Buchungszeit zuerst, wie bei der Schwester ('' vor jedem Datum).
            const zeitVon = (t: ServiceTransaction): number => {
                const ms = Date.parse(String(t.bookedAt || ''));
                return Number.isFinite(ms) ? ms : Number.NEGATIVE_INFINITY;
            };
            const zeilen = [...roh]
                .sort((a, b) => zeitVon(a.t) - zeitVon(b.t) || String(a.t.id).localeCompare(String(b.t.id)))
                .filter((z, i, alle) => alle.findIndex((x) => x.service.id === z.service.id) === i);
            const zeiten = zeilen.map((z) => Date.parse(String(z.t.bookedAt || '')));
            const zeitenBekannt = zeiten.every((z) => Number.isFinite(z));
            if (zeitenBekannt && Math.max(...zeiten) - Math.min(...zeiten) <= AUFTEILUNG_FENSTER_MS) continue;

            // Die Abrechnungslage je Tag der Zeilen, wie beim Tageszwilling:
            // deckt ein Beleg auch nur einen davon, gilt das Paar als
            // abgerechnet; sonst „unbekannt" vor „kein Beleg".
            const zeilenTage = [...new Set(zeilen.map((z) => asDay(z.t.date)).filter((d): d is string => !!d))].sort();
            const rang = (l: Abrechnungslage): number => (l.stand === 'abgerechnet' ? 0 : l.stand === 'unbekannt' ? 1 : 2);
            const pruefTage: Array<string | undefined> = zeilenTage.length ? zeilenTage : [undefined];
            const abrechnung = pruefTage
                .map((d) => abrechnungslageAmTag(d, ctx.issuedPeriods || []))
                .sort((a, b) => rang(a) - rang(b))[0];
            if (abrechnung.stand === 'abgerechnet') continue;
            const behebbar = zeilen.some((z) => !ruecknahmeGesperrt(z.service, Number(z.t.quantity) || 0, ctx.serviceLog || []));
            if (!behebbar) continue;

            // Je Position die Menge und der Betrag — welche die falsche ist,
            // weiß nur der Mensch. Die Rechnung fordert eine davon zu viel.
            const positionen: Array<{ service: ServiceItem; menge: number; betrag: number; zeiten: string[] }> = [];
            for (const z of zeilen) {
                let p = positionen.find((x) => x.service.id === z.service.id);
                if (!p) {
                    p = { service: z.service, menge: 0, betrag: 0, zeiten: [] };
                    positionen.push(p);
                }
                p.menge = round3(p.menge + (Number(z.t.quantity) || 0));
                p.zeiten.push(formatBookedAt(z.t.bookedAt));
            }
            for (const p of positionen) p.betrag = Math.round(p.menge * (Number(p.service.unitPrice) || 0) * 100) / 100;
            const preisFehlt = positionen.some((p) => !((Number(p.service.unitPrice) || 0) > 0));
            const betraege = positionen.map((p) => p.betrag).sort((a, b) => a - b);
            const nr = reportNumberOf(zeilen[0].t);
            const tag = zeilenTage[0];
            const bericht = nr === null ? `Bericht ${ref}` : `Bericht #${nr}`;
            const saetze = [
                `${bericht} ist auf ${positionen.length} Positionen gebucht: ` +
                    positionen.map((p) => `${serviceLabel(p.service)} (gebucht ${p.zeiten.join(', ')})`).join(' und ') + '.',
                preisFehlt
                    ? 'Eine Begehung gehört auf eine Position. Mindestens eine Position trägt keinen Einheitspreis; der Betrag ist deshalb unvollständig.'
                    : `Eine Begehung gehört auf eine Position — die nächste Rechnung fordert je nach falscher Position ${betraege.map(euro).join(' oder ')} netto zu viel.`,
                'Die richtige Position klären (Archiv, Durchführender) und die andere über „Buchung zurücknehmen" entfernen. War es eine bewusste Aufteilung auf Lose, den Befund quittieren.',
            ];
            out.push(
                makeFinding(ctx, {
                    ruleId: REPORT_TWO_POSITIONS_RULE_ID,
                    // Kritisch wie `doppelte_buchung`: eine falsche
                    // Geldforderung beim nächsten Beleg. Die Aufteilung, der
                    // einzige erlaubte Fall, fällt oben über die Buchungszeit
                    // heraus, und seit 05.10.2026 sperrt die App jeden anderen.
                    severity: 'kritisch',
                    scopeKind: 'report',
                    // Der Bericht allein: kommt eine dritte Position dazu,
                    // bleibt es derselbe Befund und nur der Fingerabdruck
                    // wächst.
                    scopeId: ref,
                    scopeLabel: `${bericht}${tag ? ` vom ${formatDay(tag)}` : ''} · ${positionen.map((p) => `LV ${p.service.lvPosition || '—'}`).join(' + ')}`,
                    message: saetze.join(' '),
                    expected: 1,
                    actual: positionen.length,
                    details: {
                        reportRef: ref,
                        inspectionNumber: nr,
                        date: tag ?? null,
                        serviceIds: positionen.map((p) => p.service.id),
                        lvPositions: positionen.map((p) => p.service.lvPosition ?? null),
                        titles: positionen.map((p) => p.service.title ?? null),
                        quantities: positionen.map((p) => p.menge),
                        betraegeNetto: positionen.map((p) => p.betrag),
                        preisFehlt,
                        // Die Zeilen selbst, in Buchungsreihenfolge — für den
                        // Rücknahme-Dialog.
                        entryIds: zeilen.map((z) => z.t.id),
                        bookedAt: zeilen.map((z) => z.t.bookedAt ?? null),
                        abrechnung,
                    },
                    // Nur die Zahl der Positionen — keine Beträge, keine
                    // Kennungen, keine Zeilenzahl: eine Preisänderung
                    // verschlechtert die Lage nicht, und ein Zwilling auf
                    // derselben Position steht im Journal gar nicht (er
                    // gehört zu `doppelte_buchung`).
                    fingerprint: fingerprintOf({ expected: 1, actual: positionen.length }),
                }),
            );
        }
    }

    // ── Tageszwilling ohne Berichtsbezug ─────────────────────────────────
    //
    // Vorfall 6221 (07.09.2026): auf der Begehungsposition standen am 10.03.
    // und am 24.03. je ZWEI Leistungszeilen — eine `LEGACY-AUTO`-Zeile des
    // Versandpfads (ohne reportRef, ohne Text) und die drei Monate später von
    // Hand nachgebuchte „Bericht #2 abgeschlossen" (ohne reportRef, mit
    // Nummer im Text). Das Archiv zählte 11 Berichte, der Leistungsstand 13,
    // die Rechnung hätte zwei Begehungen zu viel gefordert. `doppelte_buchung`
    // schwieg: Gleichheit wird dort am `reportRef` festgemacht, und den trägt
    // die Altzeile nicht. Auch die Drift-Sicht des Journals und Riegel 74
    // hängen an (serviceId, reportRef) — die Klasse war blind.
    //
    // Hier ist der Schlüssel der TAG, und das macht die Regel schwächer als
    // ihre Schwester: zwei Begehungen an einem Tag gibt es (Nachbegehung,
    // Abschnitte). Deshalb ist es eine Warnung, kein kritischer Befund, und
    // deshalb schweigt sie, sobald die Zeilen ZWEI Berichte nennen — über den
    // reportRef oder über die Nummer im Text („Bericht #1" und „Bericht #3"
    // am selben Tag sind zwei Begehungen). Nennen alle Zeilen denselben
    // reportRef, ist es der Fall von `doppelte_buchung` und wird dort
    // gemeldet, nicht zweimal.
    //
    // Was bewusst KEIN Befund ist:
    //  - Positionen ohne assignment 'inspection': Stunden und Zusatzleistungen
    //    haben an einem Tag legitim mehrere Zeilen.
    //  - Zeilen mit Menge > 1: das sind Sammel- und Nachtragszeilen (die
    //    Migrations-Sammelzeile trägt 23 an einem erfundenen Tag), keine
    //    einzelne Begehung.
    //  - `RPT-CHILD-…` und die Altform „Tochter-Projekt …": die Tochterbegehung
    //    im Protokoll der Mutter — sie liegt oft am Tag einer eigenen
    //    Begehung, bei der Tagespauschale sogar absichtlich (5284).
    //  - Buchungsvorgänge, ALT-Zeilen, Zeilen ohne Datum.
    //  - ein Zwilling, dessen Zusatzmenge schon abgerechnet ist
    //    (`zwillingSchonAbgerechnet`, Betreiber 02.10.2026) — wie bei der
    //    Schwester: nur noch per Storno zu beheben.
    //  - eine manuelle Verbuchung (`isManualPosting`) — ein Abrechnungsstempel,
    //    keine Begehung; wie bei der Schwester (5847: zwei Verbuchungen am
    //    selben Tag, 6099: Verbuchung neben „Bericht #15 abgeschlossen").
    //
    // Der TAG ist der Buchungstag (`buchungstag`, Europe/Berlin — dieselbe
    // Rechnung wie die Journal-Spalte `entry_day` und seit S5d jeder
    // Tagesriegel). Bis P4 (29.09.2026) war es der UTC-Textschnitt `asDay`:
    // eine Begehung um 00:30 Ortszeit lag damit am Vortag, neben den Zeilen
    // eines anderen Tages — und zwei Zeilen desselben Ortstages fielen
    // auseinander. Reine Tage und Zeitstempel ohne Zone bleiben, wie sie
    // geschrieben sind.
    //
    // Der Tag steht im Schlüssel (`scopeId` unten). Verschiebt er sich, setzt
    // der Nachtlauf den alten Befund auf erledigt und legt einen neuen an —
    // eine Quittierung bleibt am alten hängen. Das trifft einmalig die
    // Befunde, deren Zeilen zwischen 0 und 2 Uhr Ortszeit liegen; welche es
    // sind, gehört vor dem Deploy auf dem Bestand gemessen.
    for (const service of ctx.services || []) {
        if (!service?.id || service.assignment !== 'inspection') continue;
        const jeTag = new Map<string, ServiceTransaction[]>();
        for (const t of reversibleEntries(service, ctx.serviceLog || [])) {
            if (isBackfilledAltEntry(t)) continue;
            if (isManualPosting(t)) continue;
            if (String(t.reportRef || '').startsWith('RPT-CHILD-')) continue;
            // Dieselbe Tochterbuchung in der Altform (vor dem RPT-CHILD-Ref):
            // „Tochter-Projekt …: Tagespauschale gebucht." liegt bei der
            // Tagespauschale ABSICHTLICH am Tag der eigenen Begehung.
            if (/^Tochter-Projekt\b/i.test(String(t.description || ''))) continue;
            if ((Number(t.quantity) || 0) > 1 + EPS) continue;
            // Nur ISO-Daten, wie bis P4: `asDay` schnitt „01/15/2026" zu
            // Unlesbarem, `buchungstag` würde es in Gerätezeit parsen.
            if (!/^\d{4}-\d{2}-\d{2}/.test(String(t.date ?? ''))) continue;
            const tag = buchungstag(t.date);
            if (!tag || !/^\d{4}-\d{2}-\d{2}$/.test(tag)) continue;
            const liste = jeTag.get(tag);
            if (liste) liste.push(t);
            else jeTag.set(tag, [t]);
        }
        for (const [tag, zeilen] of jeTag) {
            if (zeilen.length < 2) continue;
            const refs = new Set(zeilen.map((t) => String(t.reportRef || '').trim()).filter(Boolean));
            // Zwei Berichte — zwei Begehungen.
            if (refs.size >= 2) continue;
            // Alle mit demselben reportRef: der Fall von `doppelte_buchung`.
            if (refs.size === 1 && zeilen.every((t) => String(t.reportRef || '').trim())) continue;
            // Die Berichtsnummer als SCHLÜSSEL, nicht als Zahl: „Begehung Nr.
            // 42.1" (6172, 6051) ist ein eigener Unterbericht, und
            // `reportNumberOf` liest ihn bewusst nicht — hier wäre er sonst
            // die Zeile ohne Nummer und ein Zwilling von „Bericht Nr. 16".
            const nummern = new Set(zeilen.map(berichtsSchluessel).filter((n): n is string => n !== null));
            // „Bericht #1" und „Bericht #3" am selben Tag — zwei Begehungen.
            if (nummern.size >= 2) continue;
            const sortiert = [...zeilen].sort((a, b) =>
                `${a.bookedAt || ''}|${a.id}`.localeCompare(`${b.bookedAt || ''}|${b.id}`),
            );
            const mengen = sortiert.map((t) => Number(t.quantity) || 0);
            const zuViel = round3(mengen.reduce((s, m) => s + m, 0) - Math.max(...mengen));
            const preis = Number(service.unitPrice) || 0;
            const preisFehlt = !(preis > 0);
            const betragNetto = Math.round(zuViel * preis * 100) / 100;
            const nr = nummern.size === 1 ? [...nummern][0] : null;
            // Die Abrechnungslage fragt, ob ein Beleg die Zeilen schon gefordert
            // hat — und Belegzeiträume nehmen Zeilen nach dem UTC-Textschnitt
            // (`asDay`) auf, nicht nach dem Buchungstag. 31.01. 23:30Z liegt im
            // Januar-Beleg, obwohl die Gruppe am 01.02. steht. Bei gemischten
            // Tagen wird jeder geprüft; deckt ein Beleg auch nur einen davon,
            // gilt der Zwilling als abgerechnet (und schweigt, unten), sonst
            // „unbekannt" vor „kein Beleg".
            const zeilenTage = [...new Set(sortiert.map((t) => asDay(t.date)).filter((d): d is string => !!d))].sort();
            const rang = (l: Abrechnungslage): number => (l.stand === 'abgerechnet' ? 0 : l.stand === 'unbekannt' ? 1 : 2);
            const abrechnung = (zeilenTage.length ? zeilenTage : [tag])
                .map((d) => abrechnungslageAmTag(d, ctx.issuedPeriods || []))
                .sort((a, b) => rang(a) - rang(b))[0];
            const einheit = service.unit || '';
            const beschreibung = (t: ServiceTransaction): string => {
                const text = String(t.description || '').trim() || 'ohne Beschreibung';
                const ref = String(t.bookingReference || t.reportRef || '').trim();
                return ref ? `„${text}" (${ref})` : `„${text}" (ohne Berichtsbezug)`;
            };
            const saetze = [
                `Auf ${serviceLabel(service)} stehen am ${formatDay(tag)} ${sortiert.length} Leistungszeilen, ` +
                    `ohne dass sie zwei Berichte nennen — ${round3(zuViel)} ${einheit} zu viel, wenn es eine Begehung war.`.replace('  ', ' '),
                preisFehlt
                    ? 'Die Position trägt keinen Einheitspreis; der Betrag steht deshalb auf 0,00 EUR, nicht weil nichts zu viel gefordert würde.'
                    : `Die nächste Rechnung forderte damit ${euro(betragNetto)} netto zu viel.`,
                `Zeilen: ${sortiert.map(beschreibung).join(', ')}.`,
                'Gegen das Archiv prüfen: war es eine Begehung, den Zwilling über „Buchung zurücknehmen" entfernen; waren es zwei, den Befund quittieren.',
            ];
            // Schon abgerechnet: nur noch per Storno zu beheben — kein Befund
            // (Betreiber 02.10.2026, siehe die Funktion).
            if (zwillingSchonAbgerechnet(service, mengen, abrechnung, ctx.serviceLog || [])) continue;
            out.push(
                makeFinding(ctx, {
                    ruleId: DAY_TWIN_RULE_ID,
                    severity: 'warnung',
                    scopeKind: 'service',
                    // Position UND Tag im Schlüssel: eine dritte Zeile am selben
                    // Tag hebt nur den Fingerabdruck, ein anderer Tag ist ein
                    // eigener Befund.
                    scopeId: `${service.id}|tag|${tag}`,
                    scopeLabel: `${serviceLabel(service)} · ${nr === null ? 'Begehung' : `Bericht #${nr}`} vom ${formatDay(tag)}`,
                    message: saetze.join(' '),
                    expected: 1,
                    actual: sortiert.length,
                    details: {
                        serviceId: service.id,
                        lvPosition: service.lvPosition,
                        title: service.title,
                        unit: einheit,
                        unitPrice: preis,
                        preisFehlt,
                        date: tag,
                        // Als Zahl, wo es eine ist — die Unternummer bleibt Text.
                        inspectionNumber: nr !== null && /^\d+$/.test(nr) ? Number(nr) : nr,
                        entryCount: sortiert.length,
                        entryIds: sortiert.map((t) => t.id),
                        bookedAt: sortiert.map((t) => t.bookedAt ?? null),
                        bookingReferences: sortiert.map((t) => t.bookingReference ?? null),
                        descriptions: sortiert.map((t) => t.description ?? null),
                        quantities: mengen,
                        zuViel,
                        betragNetto,
                        abrechnung,
                    },
                    fingerprint: fingerprintOf({ expected: 1, actual: sortiert.length, zuViel }),
                }),
            );
        }
    }

    // ── Eintrag ohne Leistung ────────────────────────────────────────────
    //
    // Vorfall 26021023 (Einheitenwechsel Stunden → Stück): die Altposition
    // wurde gelöscht, ihre Protokolleinträge blieben stehen und zeigten ins
    // Leere. Die Folge waren zwanzig Fehlalarme im Berichtsabgleich und
    // Buchungen, die niemanden mehr erreichten.
    const bekannt = new Set((ctx.services || []).map((s) => s.id));
    const waisen = new Map<string, ServiceTransaction[]>();
    for (const t of ctx.serviceLog || []) {
        // Ohne Bezug gibt es nichts zu suchen.
        if (!t?.serviceId) continue;
        if (bekannt.has(t.serviceId)) continue;
        // 'contract_change' ist ein Nachtragsvermerk (Menge 0), keine
        // Position — wie in cockpitEngine/cockpitWorkload. Vorfall 5882
        // (25.09.2026): der Vermerk vom 02.02. stand als gelöschte Position.
        if (t.serviceId === 'contract_change') continue;
        // Buchungsvorgänge ('BU-', 'VZ-', 'RN-', 'MB-' und die manuelle
        // Verbuchung) sind keine erbrachte Leistung. Ihr Ziel darf verschwinden,
        // ohne dass Arbeit verloren geht. 'FLAT-…' zählt dagegen MIT — die
        // Monatspauschale erhöht `quantityUsed` und ist damit Leistungsstand.
        if (isPostingEntry(t)) continue;
        const liste = waisen.get(t.serviceId);
        if (liste) liste.push(t);
        else waisen.set(t.serviceId, [t]);
    }
    for (const [serviceId, eintraege] of waisen) {
        const summe = eintraege.reduce((s, t) => s + effectiveQuantity(t), 0);
        const menge = Math.abs(summe) < EPS ? 0 : round3(summe);
        // Reihenfolgeunabhängig: die Beschriftung darf nicht davon abhängen,
        // in welcher Sortierung das Protokoll geladen wurde.
        const sortiert = [...eintraege].sort((a, b) =>
            `${asDay(a.date) || ''}|${a.id}`.localeCompare(`${asDay(b.date) || ''}|${b.id}`),
        );
        const beschreibung = sortiert.find((t) => t.description)?.description;
        out.push(
            makeFinding(ctx, {
                ruleId: ORPHAN_ENTRY_RULE_ID,
                severity: 'warnung',
                scopeKind: 'service',
                scopeId: serviceId,
                scopeLabel: beschreibung
                    ? `Gelöschte LV-Position — „${beschreibung}"`
                    : `Gelöschte LV-Position (${serviceId})`,
                message: `${eintraege.length} ${eintraege.length === 1 ? 'Protokolleintrag zeigt' : 'Protokolleinträge zeigen'} auf eine LV-Position, die es im Projekt nicht mehr gibt (Menge ${menge}).`,
                expected: 0,
                actual: menge,
                details: {
                    entryIds: sortiert.map((t) => t.id),
                    entryCount: eintraege.length,
                    quantity: menge,
                    firstDate: asDay(sortiert[0]?.date),
                    lastDate: asDay(sortiert[sortiert.length - 1]?.date),
                    // Der jüngste Eintrag dieser (gelöschten) Position. Die
                    // Buchungsvorgänge sind oben bereits heraus, hier steht
                    // also der letzte Tag ERBRACHTER Leistung.
                    abrechnung: abrechnungslageAmTag(
                        letzterLeistungstag(ctx.serviceLog, serviceId),
                        ctx.issuedPeriods || [],
                    ),
                },
                fingerprint: fingerprintOf({ expected: 0, actual: menge, eintraege: eintraege.length }),
            }),
        );
    }

    // ── Gebucht, aber nicht archiviert ───────────────────────────────────
    //
    // Vorfälle 5634 #49 und 5944 #40 (August 2026): Der Bericht ist beim
    // Kunden, die Begehung ist gebucht — und dann bricht die Versandkette ab.
    // `EmailModal.executeDbUpdate` schreibt in der Reihenfolge Projekt-JSON
    // (Buchung) → Journal-Zweitschrift → Exportstand → Archiv-Snapshot.
    // Stirbt die Seite nach dem ERSTEN Schritt — mobil der Regelfall, dort
    // friert der Hintergrund-Tab ohne Ereignis ein —, steht die Forderung in
    // der Abrechnung und der Nachweis dafür nirgends. Der Crash-Marker
    // (pendingSendPersistence) fängt genau das ab, aber er liegt im
    // localStorage AUSGERECHNET des Geräts, auf dem die App nicht wieder
    // geöffnet wurde; wird sie es nie, holt ihn niemand nach.
    //
    // Die GEGENrichtung — versendet, nicht gebucht — meldet
    // `v_unbooked_sent_reports` seit MIGRATIONS.md Nr. 45. Diese Richtung sah
    // bisher niemand: 5634 #49 lag drei Tage offen, 5944 #40 vier, und
    // aufgefallen ist es erst, weil die Begehung in der Oberfläche weiter als
    // „bearbeiten" angeboten wurde.
    //
    // Warum es Geld kostet: der Leistungsnachweis zur Rechnung entsteht aus
    // dem Archiv. Eine gebuchte Begehung ohne Archivzeile ist eine Forderung,
    // für die dem Kunden der Nachweis fehlt — und der Bericht selbst ist nur
    // noch das PDF im Sendeverlauf.
    const archivJeAbschnitt = new Map<string, Set<number>>();
    const alleArchivNummern = new Set<number>();
    for (const r of ctx.reports || []) {
        if (typeof r?.inspectionNumber !== 'number') continue;
        const key = abschnittKey(r.sectionId);
        const menge = archivJeAbschnitt.get(key);
        if (menge) menge.add(r.inspectionNumber);
        else archivJeAbschnitt.set(key, new Set([r.inspectionNumber]));
        alleArchivNummern.add(r.inspectionNumber);
    }

    const hauptbuchungen = hauptbuchungenJeBericht(ctx.serviceLog);
    for (const [key, buchung] of hauptbuchungen) {
        const archiv = archivJeAbschnitt.get(abschnittKey(buchung.sectionId));
        // Ein Abschnitt ohne EINE Archivzeile ist nicht beurteilbar: dann ist
        // unbekannt, ob hier je archiviert wurde (Alt-Import, Projekt aus der
        // Zeit vor dem Archiv). „Weiß nicht" meldet nicht — dieselbe Regel wie
        // bei der Abrechnungslage.
        if (!archiv || archiv.size === 0) continue;
        if (archiv.has(buchung.nr)) continue;
        // Ein Eintrag ohne `reportRef` (Altbestand, nur die Beschreibung) sagt
        // nicht, zu welchem Abschnitt er gehört. Eine Archivzeile mit dieser
        // Nummer in IRGENDEINEM Abschnitt reicht dann als Entwarnung: lieber
        // eine echte Lücke übersehen als einem Altbestandsprojekt mit
        // Abschnitten reihenweise Fehlalarme schreiben.
        if (!buchung.hatRef && alleArchivNummern.has(buchung.nr)) continue;
        // Die Untergrenze ist die kleinste archivierte Nummer dieses
        // Abschnitts. Alles darunter liegt vor der ersten Archivzeile und ist
        // damit die Zeit, über die dieser Abschnitt nichts aussagt.
        const kleinste = Math.min(...archiv);
        if (buchung.nr < kleinste) continue;

        const tag = asDay(buchung.date);
        out.push(
            makeFinding(ctx, {
                ruleId: GEBUCHT_OHNE_ARCHIV_RULE_ID,
                // Wie bei `versendet_ohne_buchung`: ab Tag 0 ein Mangel und
                // keine Fälligkeit. Archiviert wird beim Versand; ist es dort
                // nicht passiert, passiert es von allein nie mehr.
                severity: 'kritisch',
                scopeKind: 'report',
                // Es GIBT keine report_history.id — das ist der Befund. Der
                // Schlüssel wird deshalb aus Abschnitt und Nummer gebildet und
                // bleibt über Läufe hinweg derselbe.
                scopeId: `bericht-${key}`,
                scopeLabel: tag ? `Bericht #${buchung.nr} vom ${formatDay(tag)}` : `Bericht #${buchung.nr}`,
                message:
                    `Bericht #${buchung.nr} ist gebucht, aber nicht archiviert. ` +
                    'Die Begehung steht in der Abrechnung, im Archiv fehlt sie — der Leistungsnachweis zur Rechnung führt sie deshalb nicht auf. ' +
                    'Die Archivzeile entsteht wieder, sobald im Projekt die nächste Begehung angelegt wird; ' +
                    'sie hält dann den heutigen Stand fest, nicht den des Versandtages.',
                expected: 1,
                actual: 0,
                details: {
                    inspectionNumber: buchung.nr,
                    sectionId: buchung.sectionId,
                    reportRef: reportRefOf(buchung.nr, buchung.sectionId),
                    entryIds: buchung.entryIds,
                    date: tag,
                    /** Die höchste archivierte Nummer dieses Abschnitts — die Lücke im Bild. */
                    hoechsteArchiviert: Math.max(...archiv),
                    // Stichtag ist der Begehungstag: er entscheidet, in welchen
                    // Leistungszeitraum die gebuchte Menge fällt.
                    abrechnung: abrechnungslageAmTag(tag, ctx.issuedPeriods || []),
                },
                fingerprint: fingerprintOf({ expected: 1, actual: 0, bericht: buchung.nr }),
            }),
        );
    }

    // ── Versendet, aber weder archiviert noch gebucht ─────────────────────
    //
    // Vorfall 6051 #149 (28.09.2026): 17:55 ausgeliefert, 18:15 versendete ein
    // Tab mit dem alten Bundle. Das Version-Gate hatte ihn als veraltet
    // markiert, `saveProjectToCloud` verweigerte deshalb das Speichern — Mail
    // und Sendebericht waren da schon durch. Keine Buchung, kein Archiv, und
    // KEINE Wache sah es: `v_unbooked_sent_reports` sucht nur unter
    // archivierten Berichten, `gebucht_ohne_archiv` oben nur unter gebuchten.
    // Aufgefallen ist es einen Tag später von Hand.
    //
    // Diese Regel geht deshalb vom Sendeverlauf aus — aber nur für den
    // LAUFENDEN Bericht. Genau dort bleibt der Fall hängen: „Nächste Begehung
    // anlegen" archiviert den laufenden Bericht, bevor es hochzählt; danach
    // meldet die fehlende Buchung die Sicht. Eine ältere Nummer ohne Archiv
    // ist dagegen fast immer eine bewusst zurückgenommene (26081014 #4–#6,
    // Baustellenpause) — und deren Mails bleiben im Sendeverlauf stehen.
    //
    // Aus demselben Grund zählt nur eine Mail, die zu DIESEM Bericht gehört:
    // nicht früher als einen Tag vor seinem Begehungstag verschickt — dieselbe
    // Grenze wie `sendGuard.isStaleForReport` (sent_at ist UTC, der
    // Begehungstag der Ortstag). Zählt 26081014 wieder bis #4 hoch, ist die
    // alte Mail #4 älter als der neue Bericht und bleibt draußen.
    //
    // Gemeldet wird nur, was die beiden anderen nicht melden: ist der Bericht
    // gebucht, gehört die fehlende Archivzeile `gebucht_ohne_archiv`.
    // Nicht beurteilt wird ein Abschnitt ohne jede Archivzeile und eine
    // Nummer unter seiner kleinsten archivierten (wie oben: „weiß nicht"
    // meldet nicht).
    const laufend = ctx.currentReport;
    const laufendeNr = Number(laufend?.inspectionNumber);
    const laufenderTag = asDay(laufend?.date ?? undefined);
    const laufenderAbschnitt = abschnittKey(laufend?.sectionId);
    const abschnittsArchiv = archivJeAbschnitt.get(laufenderAbschnitt);
    const tagBeginn = laufenderTag && /^\d{4}-\d{2}-\d{2}$/.test(laufenderTag)
        ? Date.parse(`${laufenderTag}T00:00:00Z`)
        : NaN;
    // Archiviert oder gebucht heisst: im Abschnitt des laufenden Berichts —
    // oder ohne Abschnittsangabe ('all' bzw. eine Altzeile nur mit „Bericht
    // #N abgeschlossen"), weil die über den Abschnitt nichts sagt. Dieselbe
    // Nummer in einem ANDEREN Abschnitt entwarnt nicht: A/#7 vollständig und
    // B/#7 nur versendet ist ein Befund für B/#7.
    const imAbschnitt = (sectionId: string | null | undefined) => {
        const k = abschnittKey(sectionId);
        return k === laufenderAbschnitt || k === 'all';
    };
    const archiviert = !!abschnittsArchiv?.has(laufendeNr)
        || !!archivJeAbschnitt.get('all')?.has(laufendeNr);
    const gebucht = Array.from(hauptbuchungen.values())
        .some((b) => b.nr === laufendeNr && (!b.hatRef || imAbschnitt(b.sectionId)));
    if (ctx.sentReportMails && ctx.sentReportMails.length > 0
        && Number.isInteger(laufendeNr) && laufendeNr > 0
        && Number.isFinite(tagBeginn)
        && abschnittsArchiv && abschnittsArchiv.size > 0
        && laufendeNr >= Math.min(...abschnittsArchiv)
        && !archiviert
        && !gebucht) {
        const grenze = tagBeginn - 24 * 60 * 60 * 1000;
        const zeiten = ctx.sentReportMails
            .filter((m) => Number(m?.inspectionNumber) === laufendeNr)
            .filter((m) => Date.parse(m.sentAt) >= grenze)
            .map((m) => m.sentAt)
            // ISO-Zeitstempel sortieren als Text richtig.
            .sort();

        if (zeiten.length > 0) {
            const ersterVersand = zeiten[0];
            out.push(
                makeFinding(ctx, {
                    ruleId: VERSENDET_OHNE_ARCHIV_RULE_ID,
                    // Der Kunde hat den Bericht, das Haus hat weder Forderung
                    // noch Nachweis — ab Tag 0 ein Mangel.
                    severity: 'kritisch',
                    scopeKind: 'report',
                    // Es gibt weder eine Archiv- noch eine Protokollzeile, an
                    // der der Befund hängen könnte: Abschnitt und Nummer.
                    scopeId: `versand-${laufenderAbschnitt}-${laufendeNr}`,
                    scopeLabel: `Bericht #${laufendeNr}, versendet ${formatBookedAt(ersterVersand)}`,
                    message:
                        `Bericht #${laufendeNr} wurde am ${formatBookedAt(ersterVersand)} versendet, ist aber weder archiviert noch gebucht. ` +
                        'Die Mail ist beim Kunden, in Abrechnung und Leistungsnachweis fehlt die Begehung. ' +
                        'Meist ist das Speichern nach dem Versand gescheitert (Fenster mit altem Programmstand nach einem Update, App vorher geschlossen). ' +
                        'Abhilfe: Der Absender öffnet OS-IA auf dem Gerät und im Browser, mit dem er versendet hat — die App holt Buchung und Archiv dann selbst nach. ' +
                        'Geht das nicht, archiviert „Nächste Begehung anlegen" den Bericht; danach steht er in der Aufgabenliste zum Nachbuchen.',
                    expected: 1,
                    actual: 0,
                    details: {
                        inspectionNumber: laufendeNr,
                        sectionId: laufend?.sectionId ?? null,
                        inspectionDate: laufenderTag,
                        firstSentAt: ersterVersand,
                        sendCount: zeiten.length,
                        hoechsteArchiviert: Math.max(...abschnittsArchiv),
                    },
                    // Wie bei `versendet_ohne_buchung`: die Zahl der
                    // Versendungen, nicht das Alter — ein erneuter Versand ist
                    // ein neuer Vorgang und gehört erneut angesehen.
                    fingerprint: fingerprintOf({ expected: 1, actual: 0, versendungen: zeiten.length }),
                }),
            );
        }
    }

    return out.sort((a, b) => a.findingKey.localeCompare(b.findingKey));
};

// ═══════════════════════════════════════════════════════════════════════════
// 4. Versendete Mängel, die heute fehlen (Sicht Nr. 139)
// ═══════════════════════════════════════════════════════════════════════════

export const MANGEL_VERSCHWUNDEN_RULE_ID = 'versendeter_mangel_verschwunden';
export const FOTO_FEHLT_RULE_ID = 'versendetes_foto_fehlt';

/**
 * Eine Zeile der Sicht `public.v_versendete_maengel_verlust`
 * (`supabase/versendete_maengel_verlust.sql`, Nr. 139) — Spalten in der
 * Reihenfolge, die deren Abnahme (Zeile 3) zusichert.
 */
export interface VersendeterMangelVerlustRow {
    art: 'verschwunden' | 'foto_fehlt';
    project_id: string;
    project_number: string | null;
    project_name: string | null;
    item_id: string;
    ticket_number: string | null;
    title: string | null;
    status_im_bericht: string | null;
    report_history_id: string;
    inspection_number: number | null;
    report_date: string | null;
    section_id: string | null;
    /** Jüngste Spur im Löschprotokoll: Löschzeit bzw. letzte Fotoänderung. */
    letzte_spur: string | null;
    deleted_by: string | null;
    fotos_im_bericht: number;
    fehlende_fotos: number;
    fotos_heute: number;
    /** Die fehlenden Foto-Adressen, sortiert (leer bei `verschwunden`). */
    fehlende_foto_urls: string[] | null;
}

/**
 * FNV-1a (32 Bit) über die sortierten fehlenden Foto-Adressen — für den
 * Abdruck. Die ANZAHL allein reichte nicht: Foto A quittiert, später A zurück
 * und B weg, und der neue Verlust bliebe unter derselben Quittierung.
 */
const adressenAbdruck = (urls: string[] | null | undefined): number => {
    let h = 0x811c9dc5;
    for (const ch of [...(urls || [])].sort().join(String.fromCharCode(10))) {
        h ^= ch.codePointAt(0) ?? 0;
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h;
};

/**
 * Vorfall 26041008 #8 (29.09.2026) und die Flottenprüfung vom 03.10.: ein
 * älterer Stand überschrieb Projekte nach dem Versand — offene Mängel
 * verschwanden, Fotos fielen weg. Beim Kunden stand der richtige Stand, in
 * der App nicht mehr, und der nächste Bericht hätte die Lücke still
 * weitergetragen. Die Ursache schließt der Build-Riegel; diese beiden Regeln
 * melden, wenn es trotzdem geschieht.
 *
 * Gerechnet wird NICHT hier, sondern in der Sicht: sie braucht
 * `full_report_json`, und das zieht der Nachtlauf bewusst nicht flottenweit.
 * Hier wird nur die Form gemacht — wie bei `findingsFromUnbookedReports`.
 *
 *   `versendeter_mangel_verschwunden` (kritisch) — offen im letzten Bericht
 *       mit ihm, heute nicht mehr im Projekt. Ein offener Mangel, der still
 *       aus dem nächsten Bericht fällt, ist eine Gefährdung, die niemand mehr
 *       nachhält.
 *   `versendetes_foto_fehlt` (Warnung) — Fotos aus dem letzten Bericht mit
 *       dem Mangel fehlen ihm heute. Ein bewusst ersetztes Foto sieht genauso
 *       aus; der Befund endet, sobald der nächste Bericht den neuen Stand trägt.
 *
 * Der Schlüssel hängt am MANGEL (`item_id`), nicht am Bericht: derselbe
 * Mangel ist eine Aufgabe, auch wenn er in mehreren Berichten stand.
 */
export const findingsFromMaengelVerlust = (
    rows: VersendeterMangelVerlustRow[],
    /** Projekte, die nicht mehr melden (im Archiv UND ausgeglichen) — `stilleProjekte`. */
    stille: ReadonlySet<string> = new Set(),
): IntegrityFinding[] => {
    const out: IntegrityFinding[] = [];
    for (const row of rows || []) {
        if (!row?.project_id || !row.item_id || stille.has(row.project_id)) continue;
        const nummer = row.inspection_number ?? 0;
        const titel = (row.title || '').trim();
        const mangel = `Mangel #${row.ticket_number ?? '?'}${titel ? ` „${titel}“` : ''}`;
        const bericht = `Bericht #${nummer} vom ${formatDay(row.report_date)}`;
        const ctx = {
            projectId: row.project_id,
            projectNumber: row.project_number || undefined,
            projectName: row.project_name || undefined,
        };
        const details = {
            itemId: row.item_id,
            ticketNumber: row.ticket_number,
            title: row.title,
            statusImBericht: row.status_im_bericht,
            reportHistoryId: row.report_history_id,
            inspectionNumber: nummer,
            reportDate: row.report_date,
            sectionId: row.section_id,
            letzteSpur: row.letzte_spur,
            geaendertVon: row.deleted_by,
        };
        if (row.art === 'verschwunden') {
            out.push(makeFinding(ctx, {
                ruleId: MANGEL_VERSCHWUNDEN_RULE_ID,
                severity: 'kritisch',
                scopeKind: 'report',
                scopeId: row.item_id,
                scopeLabel: `${mangel} · ${bericht}`,
                message: `${mangel} stand in ${bericht} als offen beim Kunden und fehlt heute im Projekt `
                    + `(gelöscht am ${formatDay(row.letzte_spur)}). Der nächste Bericht ließe ihn still weg.`,
                expected: 1,
                actual: 0,
                details,
                // Der Bericht, aus dem er fehlt — nicht das Alter: der Befund
                // soll nicht jede Nacht neu aufleben.
                fingerprint: fingerprintOf({ bericht: nummer }),
            }));
        } else if (row.art === 'foto_fehlt' && (row.fehlende_fotos || 0) > 0) {
            const imBericht = row.fotos_im_bericht || 0;
            const fehlen = row.fehlende_fotos || 0;
            out.push(makeFinding(ctx, {
                ruleId: FOTO_FEHLT_RULE_ID,
                severity: 'warnung',
                scopeKind: 'report',
                scopeId: row.item_id,
                scopeLabel: `${mangel} · ${bericht}`,
                message: `${mangel}: ${fehlen} von ${imBericht} Fotos aus ${bericht} fehlen heute `
                    + `(zuletzt geändert am ${formatDay(row.letzte_spur)}).`,
                expected: imBericht,
                actual: imBericht - fehlen,
                details: {
                    ...details,
                    fotosImBericht: imBericht,
                    fehlendeFotos: fehlen,
                    fotosHeute: row.fotos_heute,
                    fehlendeFotoUrls: row.fehlende_foto_urls || [],
                },
                // Fehlen mehr oder ANDERE Fotos, lebt ein quittierter Befund
                // wieder auf.
                fingerprint: fingerprintOf({ bericht: nummer, fehlen, fotos: adressenAbdruck(row.fehlende_foto_urls) }),
            }));
        }
    }
    return out.sort((a, b) => a.findingKey.localeCompare(b.findingKey));
};

/**
 * Projekte, die nicht mehr melden: im Archiv UND nichts mehr abzurechnen.
 * Dieselbe Entscheidung, die `collectFindings` je Kontext trifft — für die
 * Zeilen der Sichten, die an den Kontexten vorbeilaufen.
 */
/**
 * Testprojekte (Betreiber 18.10.2026): ihre Befunde gehören nicht in den
 * Nachtlauf — Probeversand ohne Buchung u. Ä. ist dort Absicht.
 *
 * Erkannt an „Test“ als EIGENEM Wort im Projektnamen, groß/klein egal:
 * „Ronny Test“, „Test-Projekt“, „TEST 2“. Bewusst NICHT jedes Vorkommen der
 * Buchstabenfolge — „Testorf“ (Ort in Ostholstein), „Testzentrum“ oder
 * „Attest“ sind echte Projekte und fielen sonst still aus der Prüfung. Der
 * Nachtlauf nennt jede Nacht, welche Projekte er so ausgeblendet hat.
 */
export const istTestprojekt = (projektName?: string | null): boolean =>
    !!projektName && /(^|[^\p{L}\p{N}])test(?=$|[^\p{L}\p{N}])/iu.test(projektName);

export const stilleProjekte = (projects: IntegrityProjectContext[]): Set<string> => {
    const out = new Set<string>();
    for (const p of projects || []) {
        if (!meldetNoch(p.projectStatus, istAusgeglichen(p.services))) out.add(p.projectId);
    }
    return out;
};

// ═══════════════════════════════════════════════════════════════════════════
// Alles zusammen
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Alle Befunde eines Laufs, dedupliziert über `findingKey`.
 *
 * Der erste Befund zu einem Schlüssel gewinnt. Dass es überhaupt zu einer
 * Dublette kommen kann, liegt an den Zeilen der Sicht: ein Projekt taucht dort
 * je Bericht auf, und ein Bericht kann über den Mutter/Tochter-Zweig zweimal
 * geliefert werden. Zwei Zeilen für dieselbe Lage wären in der Ansicht zwei
 * Aufgaben — eine davon würde nie abgearbeitet.
 *
 * Die Ausgabe ist nach `findingKey` sortiert: der Nachtlauf schreibt sie in
 * dieser Reihenfolge fort, und zwei Läufe über dieselben Daten müssen
 * dasselbe Ergebnis liefern, egal wie die Seiten geladen wurden.
 */
export const collectFindings = (
    projects: IntegrityProjectContext[],
    unbookedRows: UnbookedSentReportRow[] = [],
    /**
     * Die Verknuepfung der Toechter, die KEINEN Kontext haben.
     *
     * Der Nachtlauf laesst Tochterprojekte aus den Kontexten heraus (jede
     * Mengenregel schluege bei ihnen an, ohne dass etwas falsch waere). Damit
     * kannte `verknuepfung` unten keine einzige Tochter, `billingModeAt` sah
     * `undefined`, und die Tagesdeckung griff NIE — 5284-1 stand mit 13 und
     * 6172-1 mit 61 Fehlalarmen da (07.09.2026), obwohl die Mutter jeden Tag
     * gebucht hatte. Ein Kontext hat Vorrang; diese Karte fuellt nur die Luecke.
     */
    tochterVerknuepfung: ReadonlyMap<string, LinkedProjectConfig | null | undefined> = new Map(),
): IntegrityFinding[] => {
    const out = new Map<string, IntegrityFinding>();
    const add = (findings: IntegrityFinding[]) => {
        findings.forEach((f) => {
            if (!out.has(f.findingKey)) out.set(f.findingKey, f);
        });
    };
    // Die Leistungszeiträume der geladenen Projekte, damit auch die Zeilen der
    // Sicht ihre Abrechnungslage bekommen — sie führt keine Belege mit sich.
    // Projekte, die dieser Lauf nicht geladen hat (Tochter, nicht ladbar),
    // stehen nicht darin und bleiben `unbekannt`.
    const perioden = new Map<string, IssuedPeriod[]>();
    /** Projekte, die nicht mehr melden (im Archiv UND ausgeglichen). */
    const stille = new Set<string>();
    /** Protokoll und Begehungspositionen je Projekt — fuer die Tagespauschale. */
    const protokolle = new Map<string, ServiceTransaction[]>();
    const inspektionsPositionen = new Map<string, Set<string>>();
    const verknuepfung = new Map<string, LinkedProjectConfig | null | undefined>();
    for (const p of projects || []) {
        protokolle.set(p.projectId, p.serviceLog || []);
        inspektionsPositionen.set(
            p.projectId,
            new Set((p.services || []).filter((s) => s.assignment === 'inspection').map((s) => s.id)),
        );
        verknuepfung.set(p.projectId, p.linkedConfig);
    }
    for (const p of projects || []) {
        // Die Zeitraeume werden AUCH von abgeschlossenen Projekten gesammelt:
        // eine Tochter kann offen sein, waehrend ihre Mutter abgeschlossen ist,
        // und ohne den Beleg der Mutter stuende die Abrechnungslage der Tochter
        // auf `unbekannt`.
        if (p.issuedPeriods) perioden.set(p.projectId, p.issuedPeriods);
        if (!meldetNoch(p.projectStatus, istAusgeglichen(p.services))) {
            // Im Archiv und nichts mehr abzurechnen — dieses Projekt meldet
            // nicht mehr. Gemerkt wird es trotzdem, weil die Zeilen der Sicht
            // unten an dieser Schleife vorbeilaufen und denselben Riegel
            // brauchen.
            stille.add(p.projectId);
            continue;
        }
        if (p.check) add(findingsFromProjectCheck(p, p.check));
        add(findingsFromExtraRules(p));
    }
    // Tochter mit Tagespauschale, deren Tag die Mutter bereits gebucht hat:
    // kein Befund. Der Riegel der Sicht kann das nicht sehen — er sucht einen
    // `RPT-CHILD-`-Eintrag, den der richtige Ablauf hier nie schreibt.
    const gedeckt = new Set<string>();
    for (const row of unbookedRows || []) {
        const mutter = row.parent_project_id;
        if (!mutter) continue;
        // Nach der Art, die am BEGEHUNGSTAG galt — nicht nach der heutigen.
        // Sonst machte ein Umschalten auf „einzeln" jede frueher gedeckte
        // Begehung schlagartig wieder zu einer offenen Forderung.
        const konfiguration = verknuepfung.has(row.project_id)
            ? verknuepfung.get(row.project_id)
            : tochterVerknuepfung.get(row.project_id);
        if (billingModeAt(konfiguration, row.inspection_date) !== TAGESPAUSCHALE) continue;
        if (istTagGedeckt(protokolle.get(mutter), inspektionsPositionen.get(mutter) || new Set(), row.inspection_date)) {
            gedeckt.add(`${row.project_id}:${row.inspection_number}`);
        }
    }
    add(findingsFromUnbookedReports(unbookedRows, perioden, stille, gedeckt));
    return [...out.values()].sort((a, b) => a.findingKey.localeCompare(b.findingKey));
};
